import React, { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Avatar, IconButton } from "@mui/material";
import Pagination from "react-js-pagination";
import ModalBox from "../../components/modal/ModalBox";
import Switch from "../../components/Switch/Switch";
import NotFound from "../../components/not-found/NotFound";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import useDebounce from "../../hooks/useDebounce";
import common from "../../routes/common";
import { toastMessage } from "../../utils/toastMessage";
import findSerialNumber from "../../utils/findSerialNumber";
import {
  addEcommerceApi,
  cancelEcommerceOrderApi,
  changeEcommerceStatusApi,
  deleteEcommerceApi,
  fetchEcommerceApi,
  shiprocketEcommerceApi,
  updateEcommerceApi,
} from "../../services/ecommerceManagement";
import { fetchConsultantListApi } from "../../services/consultantManagement";
import { getUserListApi } from "../../services/userManagement";

const { NOT_FOUND } = common;

const tabs = [
  { key: "product", label: "Products" },
  { key: "category", label: "Categories" },
  { key: "banner", label: "Banners" },
  { key: "offer", label: "Offers" },
  { key: "coupon", label: "Coupons" },
  { key: "review", label: "Reviews" },
  { key: "order", label: "Orders" },
  { key: "tracking", label: "Tracking" },
  { key: "appPreview", label: "App Preview" },
];

const ADMIN_PRIMARY = "#FF8989";
const ADMIN_PRIMARY_DARK = "#f36f6f";
const ADMIN_PRIMARY_SOFT = "#FFF5F5";
const CANCELLABLE_ORDER_STATUSES = ["PLACED", "CONFIRMED", "PACKED"];
const BLOCKED_CANCEL_ORDER_STATUSES = ["CANCELLED", "SHIPPED", "OUT_FOR_DELIVERY", "DELIVERED", "RETURNED", "RTO"];
const BLOCKED_CANCEL_SHIPPING_STATUSES = ["SHIPPED", "IN_TRANSIT", "PICKED_UP", "OUT_FOR_DELIVERY", "DELIVERED", "RTO", "RETURNED", "CANCELLED", "CANCELED"];

const initialForms = {
  category: {
    title: "",
    description: "",
    icon: "",
    sortOrder: 0,
    status: true,
  },
  product: {
    title: "",
    sku: "",
    categoryId: "",
    shortDescription: "",
    description: "",
    price: "",
    compareAtPrice: "",
    androidPrice: "",
    androidCompareAtPrice: "",
    iosPrice: "",
    iosCompareAtPrice: "",
    gstPercent: "",
    stock: "",
    tags: "",
    addons: [],
    userScope: "ALL_USERS",
    userIds: "",
    sortOrder: 0,
    isFeatured: false,
    status: true,
    existingImages: [],
  },
  banner: {
    title: "",
    subtitle: "",
    ctaText: "",
    productId: "",
    categoryId: "",
    startsAt: "",
    endsAt: "",
    sortOrder: 0,
    status: true,
  },
  offer: {
    title: "",
    description: "",
    scope: "PRODUCT",
    productIds: "",
    categoryIds: "",
    discountType: "PERCENTAGE",
    discountValue: "",
    userScope: "ALL_USERS",
    userIds: "",
    startsAt: "",
    endsAt: "",
    priority: 0,
    status: true,
  },
  coupon: {
    code: "",
    title: "",
    description: "",
    scope: "PRODUCT",
    productIds: "",
    categoryIds: "",
    discountType: "PERCENTAGE",
    discountValue: "",
    minOrderAmount: "",
    usageLimit: "",
    consultantScope: "NO_CONSULTANT",
    consultantIds: "",
    consultantShareType: "NONE",
    consultantShareValue: "",
    consultantShareOn: "TOTAL_PRICE",
    minConsultationMinutes: "25",
    userScope: "ALL_USERS",
    userIds: "",
    startsAt: "",
    endsAt: "",
    status: true,
  },
  review: {
    productId: "",
    reviewerName: "",
    reviewerLocation: "",
    rating: "5",
    title: "",
    comment: "",
    isVerifiedBuyer: true,
    reviewDate: "",
    sortOrder: 0,
    status: true,
    existingImages: [],
  },
};

const DATETIME_FIELDS = ["startsAt", "endsAt", "reviewDate"];
const DISPLAY_TIME_ZONE = "Asia/Kolkata";

const istDateParts = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: DISPLAY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const get = (type) => parts.find((part) => part.type === type)?.value;
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
  };
};

const dateInputValue = (value) => {
  if (!value) return "";
  const parts = istDateParts(value);
  if (!parts) return "";
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

const dateAfterDays = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return dateInputValue(date);
};

const localDateTimeToIso = (value) => {
  if (!value) return "";
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute] = match;
    return new Date(`${year}-${month}-${day}T${hour}:${minute}:00+05:30`).toISOString();
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString();
};

const withIsoDateTimes = (payload) => {
  DATETIME_FIELDS.forEach((key) => {
    if (payload[key] !== undefined && payload[key] !== null && payload[key] !== "") {
      payload[key] = localDateTimeToIso(payload[key]);
    }
  });
  return payload;
};

const formatDateTime = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleString("en-IN", {
    timeZone: DISPLAY_TIME_ZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getTimeLeft = (value, now = Date.now()) => {
  if (!value) return "No expiry";
  const endDate = new Date(value);
  if (Number.isNaN(endDate.getTime())) return "No expiry";
  const diff = endDate.getTime() - now;
  if (diff <= 0) return "Expired";
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m ${seconds}s left`;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s left`;
  if (minutes > 0) return `${minutes}m ${seconds}s left`;
  return `${seconds}s left`;
};

const commaIds = (items = []) => items.map((item) => item?._id || item).filter(Boolean).join(",");
const splitIds = (value = "") =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const extractList = (response) => {
  const result = response?.data?.result || response?.result || {};
  return result.list || result.docs || result.data || result.consultants || result.users || [];
};

const personLabel = (item = {}) => {
  const record = item || {};
  return record.name ||
    record.accountName ||
    record.details?.name ||
    record.details?.email ||
    record.email ||
    record.details?.phone ||
    record.phone ||
    record._id ||
    "Unknown";
};

const personSearchText = (item = {}) =>
  [
    item.label,
    item.value,
    item.publicId,
    item.userId,
    item.name,
    item.accountName,
    item.email,
    item.phone,
    item.details?.name,
    item.details?.email,
    item.details?.phone,
    item.details?.phonePrefix,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const personPublicId = (item = {}) => item.userId || item.publicId || item.referId || "";

const userPhoneNumber = (user = {}) => (
  user?.details?.phone ||
  user?.phone ||
  user?.mobile ||
  user?.details?.mobile ||
  user?.details?.phoneNumber ||
  user?.phoneNumber ||
  ""
);

const styles = {
  tabs: { display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 18 },
  tab: {
    border: `1px solid ${ADMIN_PRIMARY}`,
    background: "#fff",
    padding: "9px 14px",
    borderRadius: 6,
    color: ADMIN_PRIMARY_DARK,
    fontWeight: 700,
  },
  activeTab: { background: ADMIN_PRIMARY, color: "#fff", borderColor: ADMIN_PRIMARY },
  formGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16 },
  modalLayout: { display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 16, alignItems: "start" },
  modalLayoutWithPreview: { display: "grid", gridTemplateColumns: "minmax(720px, 1.4fr) minmax(390px, 0.8fr)", gap: 20, alignItems: "start" },
  full: { gridColumn: "1 / -1" },
  label: { fontSize: 13, fontWeight: 700, color: "#333", marginBottom: 5 },
  input: { width: "100%", border: "1px solid #ddd", borderRadius: 6, padding: "9px 10px" },
  textarea: { width: "100%", border: "1px solid #ddd", borderRadius: 6, padding: "9px 10px", minHeight: 76 },
  help: { display: "block", fontSize: 12, color: "#777", marginTop: 4 },
  badge: { padding: "4px 9px", borderRadius: 5, background: ADMIN_PRIMARY_SOFT, color: ADMIN_PRIMARY_DARK, fontWeight: 700 },
  previewPanel: { border: `1px solid ${ADMIN_PRIMARY}`, borderRadius: 8, padding: 14, background: ADMIN_PRIMARY_SOFT, position: "sticky", top: 0 },
  phone: { width: "100%", borderRadius: 22, background: "#fff", border: `1px solid ${ADMIN_PRIMARY}`, overflow: "hidden", boxShadow: "0 8px 24px rgba(255, 137, 137, 0.18)" },
  phoneBody: { padding: 14 },
  previewImage: { width: "100%", aspectRatio: "1 / 0.72", objectFit: "cover", borderRadius: 10, background: ADMIN_PRIMARY_SOFT },
  previewTitle: { color: ADMIN_PRIMARY_DARK, fontWeight: 800, fontSize: 16, margin: "8px 0 4px" },
  previewPrice: { color: "#222", fontWeight: 900, fontSize: 17 },
  previewButton: { width: "100%", border: 0, borderRadius: 6, background: ADMIN_PRIMARY, color: "#fff", padding: "10px 12px", fontWeight: 800 },
  addonRow: { display: "grid", gridTemplateColumns: "1fr 110px 38px", gap: 8, alignItems: "end", marginBottom: 8 },
  testDataButton: { border: `1px solid ${ADMIN_PRIMARY}`, background: ADMIN_PRIMARY_SOFT, color: ADMIN_PRIMARY_DARK, borderRadius: 6, padding: "8px 12px", fontWeight: 800, marginBottom: 14 },
  meaningNote: { border: "1px solid #ffd1d1", background: ADMIN_PRIMARY_SOFT, color: "#555", borderRadius: 6, padding: "10px 12px", marginBottom: 14, fontSize: 13 },
  multiSelect: { width: "100%", border: "1px solid #ddd", borderRadius: 6, padding: 8, minHeight: 112 },
  previewSectionTitle: { color: "#333", fontSize: 12, fontWeight: 900, margin: "0 0 8px" },
  productCardGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 },
  productCard: { background: "#fff", border: "1px solid #ffe1e1", borderRadius: 8, padding: 8 },
  productPreviewStack: { display: "grid", gap: 18 },
  appPreviewSection: { border: `1px solid ${ADMIN_PRIMARY}`, borderRadius: 8, padding: 14, background: ADMIN_PRIMARY_SOFT, marginBottom: 18 },
  appPreviewGrid: { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 16, alignItems: "start" },
  previewToggle: { width: "100%", border: `1px solid ${ADMIN_PRIMARY}`, background: ADMIN_PRIMARY_SOFT, color: "#111", borderRadius: 6, padding: "10px 12px", fontWeight: 900, textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 },
  selectorBox: { border: "1px solid #d9d9d9", borderRadius: 8, background: "#fff", overflow: "hidden" },
  selectorSearch: { width: "100%", border: 0, borderBottom: "1px solid #d9d9d9", padding: "11px 12px", outline: "none" },
  selectorTableWrap: { maxHeight: 230, overflowY: "auto" },
  selectorTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  selectorHead: { background: ADMIN_PRIMARY_SOFT, color: "#333", textAlign: "left" },
  selectorCell: { padding: "8px 10px", border: "1px solid #e5e5e5", verticalAlign: "top" },
  selectorId: { display: "block", color: "#777", fontSize: 11, marginTop: 2, wordBreak: "break-all" },
  selectorEmpty: { padding: 12, color: "#777", fontSize: 12 },
  selectorSummary: { padding: 10, color: "#555", fontSize: 12, borderTop: "1px solid #e5e5e5", background: "#fafafa" },
  jsonBlock: { margin: "6px 0 0", padding: 8, background: "#fafafa", border: "1px solid #e5e5e5", borderRadius: 6, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: 12, maxWidth: 420 },
  trackingHistory: { background: "#fff", border: "1px solid #e5e5e5", borderRadius: 8, padding: 12 },
  trackingEvent: { display: "grid", gridTemplateColumns: "180px 1fr 1fr", gap: 12, padding: "10px 0", borderBottom: "1px solid #eee" },
  linkButton: { border: `1px solid ${ADMIN_PRIMARY}`, background: "#fff", color: ADMIN_PRIMARY_DARK, borderRadius: 6, padding: "7px 10px", fontWeight: 800 },
  orderItems: { display: "grid", gap: 8, minWidth: 320 },
  orderItem: { display: "grid", gridTemplateColumns: "44px 1fr", gap: 9, padding: 8, border: "1px solid #eee", borderRadius: 8, background: "#fff" },
  orderItemImage: { width: 44, height: 44, borderRadius: 6, objectFit: "cover", background: ADMIN_PRIMARY_SOFT },
  orderAddon: { marginTop: 4, padding: "3px 7px", borderRadius: 4, background: ADMIN_PRIMARY_SOFT, color: ADMIN_PRIMARY_DARK, display: "inline-block", marginRight: 5, fontSize: 11, fontWeight: 700 },
  orderFinalPrice: { margin: "8px 0", padding: "7px 9px", borderRadius: 6, background: ADMIN_PRIMARY_SOFT, color: ADMIN_PRIMARY_DARK, fontWeight: 900 },
  productNameCell: { minWidth: 0 },
  productTitle: { display: "block", color: "#111", fontSize: 13, fontWeight: 800, marginBottom: 6 },
  productDescriptionClamp: { color: "#334155", fontSize: 12, lineHeight: 1.45, maxWidth: 560, whiteSpace: "normal", overflowWrap: "anywhere" },
  productCategoryCell: { color: "#111827", fontSize: 12, lineHeight: 1.45, minWidth: 128 },
  productPriceCell: { minWidth: 680, maxWidth: 720 },
  productStockCell: { textAlign: "center", fontWeight: 700, color: "#111827" },
  priceLedger: { width: "100%", minWidth: 660, border: "1px solid #f3b4bc", borderRadius: 7, overflow: "hidden", background: "transparent" },
  priceLedgerTitle: { display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", padding: "8px 10px", background: "rgba(255, 245, 245, 0.38)", borderBottom: "1px solid #f3b4bc" },
  priceLedgerTitleText: { color: "#334155", fontSize: 11, fontWeight: 650, textTransform: "uppercase" },
  priceLedgerGst: { color: "#475569", fontSize: 10, fontWeight: 650, whiteSpace: "nowrap" },
  priceLedgerGrid: { display: "grid", gridTemplateColumns: "78px 88px 88px 95px 100px 100px 88px", minWidth: 637 },
  priceLedgerHead: { padding: "7px 6px", color: "#334155", background: "rgba(255, 248, 239, 0.45)", borderRight: "1px solid #d7e4f2", borderBottom: "1px solid #f3b4bc", fontSize: 10, fontWeight: 600, textAlign: "center" },
  priceLedgerCell: { minHeight: 50, padding: "8px 6px", borderRight: "1px solid #d7e4f2", borderBottom: "1px solid #d7e4f2", color: "#111827", fontSize: 11, lineHeight: 1.28, textAlign: "center", display: "flex", flexDirection: "column", justifyContent: "center", gap: 2 },
  priceLedgerPlatform: { alignItems: "flex-start", textAlign: "left", fontWeight: 600 },
  priceLedgerValue: { color: "#111827", fontWeight: 600 },
  priceLedgerMuted: { color: "#64748b", fontSize: 9, fontWeight: 500 },
  priceLedgerFinal: { color: ADMIN_PRIMARY_DARK, fontWeight: 650 },
  priceRuleStrip: { display: "flex", gap: 6, flexWrap: "wrap", padding: "8px 10px", background: "rgba(248, 250, 252, 0.35)", borderTop: "1px solid #f3b4bc" },
  priceRuleChip: { padding: "5px 7px", border: "1px solid #d7e4f2", borderRadius: 5, background: "transparent", color: "#334155", fontSize: 10, lineHeight: 1.3 },
  shiprocketActions: { display: "grid", gap: 6, minWidth: 130 },
  shiprocketButton: { border: `1px solid ${ADMIN_PRIMARY}`, background: "#fff", color: ADMIN_PRIMARY_DARK, borderRadius: 5, padding: "6px 8px", fontSize: 12, fontWeight: 800, cursor: "pointer" },
  shiprocketButtonPrimary: { background: ADMIN_PRIMARY, color: "#fff", borderColor: ADMIN_PRIMARY },
  shiprocketButtonDanger: { border: "1px solid #c62828", background: "#fff", color: "#c62828", borderRadius: 5, padding: "6px 8px", fontSize: 12, fontWeight: 800, cursor: "pointer" },
  shiprocketLink: { color: ADMIN_PRIMARY_DARK, fontSize: 12, fontWeight: 900, textDecoration: "underline" },
  shipmentBox: { marginTop: 9, padding: "8px 9px", border: "1px solid #ffe1e1", borderRadius: 6, background: "#fff" },
  appHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", color: "#7a0000", fontWeight: 900 },
  appBanner: { borderRadius: 10, padding: 14, marginBottom: 14, minHeight: 96, background: `linear-gradient(135deg, ${ADMIN_PRIMARY_DARK}, #7a0000)`, color: "#fff" },
  appCategoryRow: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 14 },
  appCategoryChip: { border: "1px solid #f2e6e1", borderRadius: 8, padding: "8px 4px", textAlign: "center", background: "#fff", color: "#806a65", fontSize: 10 },
  appCategoryChipActive: { background: "#7a0000", color: "#fff", borderColor: "#7a0000" },
  appBottomNav: { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", borderTop: "1px solid #f4e9e4", padding: "8px 0", color: "#7a0000", fontSize: 10, textAlign: "center" },
  detailText: { margin: "4px 0", color: "#333", fontSize: 13, lineHeight: 1.45 },
  countdownBadge: { display: "inline-block", marginTop: 5, padding: "4px 8px", borderRadius: 5, background: ADMIN_PRIMARY_SOFT, color: ADMIN_PRIMARY_DARK, fontSize: 13, fontWeight: 900 },
  imageList: { display: "grid", gridTemplateColumns: "1fr", gap: 10, marginTop: 10, width: "100%" },
  imageItem: { display: "flex", gap: 10, alignItems: "center", border: "1px solid #eee", borderRadius: 8, padding: 8, background: "#fff", minWidth: 0 },
  imageThumb: { width: 48, height: 48, objectFit: "cover", borderRadius: 6, background: ADMIN_PRIMARY_SOFT },
  imageName: { color: "#555", fontSize: 12, minWidth: 0, flex: 1, overflow: "hidden" },
  imageTitle: { display: "block", color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  imageFileName: { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  imageRemove: { border: `1px solid ${ADMIN_PRIMARY}`, background: ADMIN_PRIMARY_SOFT, color: ADMIN_PRIMARY_DARK, borderRadius: 5, padding: "6px 9px", fontWeight: 800 },
};

const Field = ({ label, children, full, help }) => (
  <div style={full ? styles.full : undefined}>
    <div style={styles.label}>{label}</div>
    {children}
    {help && <small style={styles.help}>{help}</small>}
  </div>
);

const EcommerceManagement = () => {
  const [activeTab, setActiveTab] = useState("product");
  const [list, setList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [search, setSearch] = useState("");
  const [consultantSearch, setConsultantSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [showModalPreview, setShowModalPreview] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForms.product);
  const [image, setImage] = useState(null);
  const [images, setImages] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [trackingExpanded, setTrackingExpanded] = useState({});
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const debounceSearch = useDebounce(search, 300);
  const debounceConsultantSearch = useDebounce(consultantSearch, 350);
  const debounceUserSearch = useDebounce(userSearch, 350);
  const limit = 10;

  const categoryOptions = useMemo(() => categories.map((item) => ({ value: item._id, label: item.title })), [categories]);
  const productOptions = useMemo(() => products.map((item) => ({ value: item._id, label: item.title })), [products]);
  const consultantOptions = useMemo(() => consultants.filter((item) => item?._id).map((item) => ({ value: item._id, label: personLabel(item), publicId: personPublicId(item), raw: item })), [consultants]);
  const userOptions = useMemo(() => users.filter((item) => item?._id).map((item) => ({ value: item._id, label: personLabel(item), publicId: personPublicId(item), raw: item })), [users]);
  const categoryTitle = useMemo(
    () => categoryOptions.find((item) => item.value === form.categoryId)?.label || "Category",
    [categoryOptions, form.categoryId]
  );
  const previewImage = useMemo(() => {
    const file = images?.[0] || image;
    if (file) return URL.createObjectURL(file);
    if (activeTab === "product") return selected?.primaryImageUrl || selected?.imageUrls?.[0] || "";
    if (activeTab === "category" || activeTab === "banner") return selected?.imageUrl || "";
    return "";
  }, [activeTab, image, images, selected]);
  const selectedImagePreviews = useMemo(
    () => images.map((file) => ({ file, url: URL.createObjectURL(file) })),
    [images]
  );
  const existingManagedImages = useMemo(() => {
    if (!["product", "review"].includes(activeTab)) return [];
    return (form.existingImages || []).map((imageKey) => {
      const selectedIndex = (selected?.images || []).findIndex((item) => item === imageKey);
      const url = imageKey?.startsWith?.("http")
        ? imageKey
        : selected?.imageUrls?.[selectedIndex] || selected?.primaryImageUrl || "";
      return { key: imageKey, url };
    });
  }, [activeTab, form.existingImages, selected]);

  useEffect(() => {
    return () => {
      if (previewImage?.startsWith("blob:")) URL.revokeObjectURL(previewImage);
    };
  }, [previewImage]);

  useEffect(() => {
    return () => {
      selectedImagePreviews.forEach((item) => URL.revokeObjectURL(item.url));
    };
  }, [selectedImagePreviews]);

  const fetchList = async () => {
    try {
      showLoader();
      const { data } = await fetchEcommerceApi({
        type: activeTab,
        page: activePage,
        limit,
        ...(debounceSearch ? { search: debounceSearch } : {}),
      });
      if (data.code === 200) {
        setList(data?.result?.list || []);
        setTotal(data?.result?.total || 0);
      } else {
        setList([]);
        setTotal(0);
        toastMessage(data.message, "error", "ecommerceList");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      hideLoader();
    }
  };

  const fetchLookups = async () => {
    const [categoryRes, productRes, offerRes, couponRes, consultantRes, userRes] = await Promise.allSettled([
      fetchEcommerceApi({ type: "category", page: 1, limit: 200, status: true }),
      fetchEcommerceApi({ type: "product", page: 1, limit: 300, status: true }),
      fetchEcommerceApi({ type: "offer", page: 1, limit: 300, status: true }),
      fetchEcommerceApi({ type: "coupon", page: 1, limit: 300, status: true }),
      fetchConsultantListApi({ page: "1", limit: "300", filter: {} }),
      getUserListApi({ page: "1", limit: "300", filter: {} }),
    ]);
    if (categoryRes.status === "fulfilled") setCategories(categoryRes.value?.data?.result?.list || []);
    if (productRes.status === "fulfilled") setProducts(productRes.value?.data?.result?.list || []);
    if (offerRes.status === "fulfilled") setOffers(offerRes.value?.data?.result?.list || []);
    if (couponRes.status === "fulfilled") setCoupons(couponRes.value?.data?.result?.list || []);
    if (consultantRes.status === "fulfilled") setConsultants(extractList(consultantRes.value));
    if (userRes.status === "fulfilled") setUsers(extractList(userRes.value));
    [categoryRes, productRes, offerRes, couponRes, consultantRes, userRes].forEach((result) => {
      if (result.status === "rejected") console.log("lookup error", result.reason);
    });
  };

  const mergeById = (currentItems, nextItems) => {
    const map = new Map();
    [...currentItems, ...nextItems].forEach((item) => {
      if (item?._id) map.set(item._id, item);
    });
    return Array.from(map.values());
  };

  const fetchPeopleLookup = async (kind, searchValue) => {
    if (!searchValue || String(searchValue).trim().length < 2) return;
    try {
      const request = {
        page: "1",
        limit: "50",
        search: String(searchValue).trim(),
        filter: {},
      };
      if (kind === "consultant") {
        const { data } = await fetchConsultantListApi(request);
        if (data.code === 200) setConsultants((prev) => mergeById(prev, extractList({ data })));
      } else {
        const { data } = await getUserListApi(request);
        if (data.code === 200) setUsers((prev) => mergeById(prev, extractList({ data })));
      }
    } catch (error) {
      console.log(`${kind} lookup error`, error);
    }
  };

  useEffect(() => {
    fetchLookups();
  }, []);

  useEffect(() => {
    if (form.consultantScope === "SPECIFIC_CONSULTANTS") {
      fetchPeopleLookup("consultant", debounceConsultantSearch);
    }
  }, [debounceConsultantSearch, form.consultantScope]);

  useEffect(() => {
    if (form.userScope === "SPECIFIC_USERS") {
      fetchPeopleLookup("user", debounceUserSearch);
    }
  }, [debounceUserSearch, form.userScope]);

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (activeTab === "appPreview") return;
    setTrackingExpanded({});
    fetchList();
  }, [activeTab, activePage, debounceSearch]);

  const openAdd = () => {
    setSelected(null);
    const base = { ...initialForms[activeTab] };
    if (activeTab === "banner" || activeTab === "offer" || activeTab === "coupon") {
      base.startsAt = dateAfterDays(0);
      base.endsAt = dateAfterDays(activeTab === "coupon" ? 14 : 7);
    }
    if (activeTab === "review") {
      base.reviewDate = dateInputValue(new Date());
    }
    setForm(base);
    setImage(null);
    setImages([]);
    setShowModalPreview(false);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setSelected(item);
    const base = { ...initialForms[activeTab], ...item };
    if (activeTab === "product") {
      base.categoryId = item.categoryId?._id || item.categoryId || "";
      base.tags = (item.tags || []).join(",");
      base.addons = item.addons?.length ? item.addons : [];
      base.existingImages = item.images || [];
      base.userScope = item.userScope || "ALL_USERS";
      base.userIds = commaIds(item.userIds);
    }
    if (activeTab === "banner") {
      base.categoryId = item.categoryId?._id || item.categoryId || "";
      base.productId = item.productId?._id || item.productId || "";
      base.startsAt = dateInputValue(item.startsAt);
      base.endsAt = dateInputValue(item.endsAt);
    }
    if (activeTab === "offer" || activeTab === "coupon") {
      base.productIds = commaIds(item.productIds);
      base.categoryIds = "";
      base.scope = "PRODUCT";
      base.userIds = commaIds(item.userIds);
      base.startsAt = dateInputValue(item.startsAt);
      base.endsAt = dateInputValue(item.endsAt);
      if (activeTab === "coupon") {
        base.consultantScope = item.consultantScope === "SPECIFIC_CONSULTANTS" ? "SPECIFIC_CONSULTANTS" : "NO_CONSULTANT";
        base.consultantIds = commaIds(item.consultantIds);
        base.minConsultationMinutes = item.minConsultationMinutes ?? 25;
      }
    }
    if (activeTab === "review") {
      base.productId = item.productId?._id || item.productId || "";
      base.reviewDate = dateInputValue(item.reviewDate);
      base.existingImages = item.images || [];
    }
    setForm(base);
    setImage(null);
    setImages([]);
    setShowModalPreview(false);
    setModalOpen(true);
  };

  const updateForm = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const addManagedImages = (fileList = []) => {
    const nextFiles = Array.from(fileList || []);
    if (!nextFiles.length) return;
    setImages((prev) => [...prev, ...nextFiles]);
  };

  const removeNewManagedImage = (index) => {
    setImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const removeExistingManagedImage = (imageKey) => {
    setForm((prev) => ({
      ...prev,
      existingImages: (prev.existingImages || []).filter((item) => item !== imageKey),
    }));
  };

  const updateConsultantScope = (value) => {
    setForm((prev) => ({
      ...prev,
      consultantScope: value,
      ...(value === "SPECIFIC_CONSULTANTS"
        ? {
          minConsultationMinutes: prev.minConsultationMinutes || "25",
        }
        : {
          consultantIds: "",
          consultantShareType: "NONE",
          consultantShareValue: "",
          consultantShareOn: "TOTAL_PRICE",
          minConsultationMinutes: "0",
        }),
    }));
  };

  const addAddon = () => {
    updateForm("addons", [...(form.addons || []), { title: "", price: "", description: "", status: true }]);
  };

  const updateAddon = (index, key, value) => {
    const nextAddons = [...(form.addons || [])];
    nextAddons[index] = { ...nextAddons[index], [key]: value };
    updateForm("addons", nextAddons);
  };

  const removeAddon = (index) => {
    updateForm("addons", (form.addons || []).filter((_, itemIndex) => itemIndex !== index));
  };

  const fillTestData = () => {
    const firstCategoryId = categories?.[0]?._id || "";
    const firstProductId = products?.[0]?._id || "";
    const firstConsultantId = consultants?.[0]?._id || "";
    const firstUserId = users?.[0]?._id || "";

    const testData = {
      category: {
        ...initialForms.category,
        title: "Rudraksha",
        description: "Sacred Rudraksha products for protection, focus, and spiritual balance.",
        icon: "RD",
        sortOrder: 1,
      },
      product: {
        ...initialForms.product,
        title: "Siddha Rudraksha Mala",
        sku: "RUD-MALA-001",
        categoryId: firstCategoryId,
        shortDescription: "Energized Rudraksha mala for peace, clarity, and daily sadhana.",
        description: "Premium Rudraksha mala prepared with careful quality checks and optional Vedic energization.",
        price: "12500",
        compareAtPrice: "15000",
        androidPrice: "12500",
        androidCompareAtPrice: "15000",
        iosPrice: "12900",
        iosCompareAtPrice: "15400",
        gstPercent: "18",
        stock: "25",
        tags: "rudraksha,mala,spiritual",
        userScope: firstUserId ? "SPECIFIC_USERS" : "ALL_USERS",
        userIds: firstUserId,
        addons: [
          { title: "Vedic Energization", price: "500", description: "", status: true },
          { title: "Authenticity Certificate", price: "250", description: "", status: true },
          { title: "Premium Silk Packaging", price: "150", description: "", status: true },
        ],
        sortOrder: 1,
        isFeatured: true,
      },
      banner: {
        ...initialForms.banner,
        title: "50% OFF",
        subtitle: "DIVINE OFFER",
        ctaText: "Shop Now",
        categoryId: firstCategoryId,
        productId: firstProductId,
        startsAt: dateAfterDays(0),
        endsAt: dateAfterDays(7),
        sortOrder: 1,
      },
      offer: {
        ...initialForms.offer,
        title: "Limited Time Divine Offer",
        description: "Auto-applied discount for selected sacred products.",
        scope: "PRODUCT",
        productIds: firstProductId,
        categoryIds: "",
        discountType: "PERCENTAGE",
        discountValue: "20",
        userScope: firstUserId ? "SPECIFIC_USERS" : "ALL_USERS",
        userIds: firstUserId,
        startsAt: dateAfterDays(0),
        endsAt: dateAfterDays(7),
        priority: 10,
      },
      coupon: {
        ...initialForms.coupon,
        code: "DIVINE20",
        title: "Divine Store Coupon",
        description: "Coupon for ecommerce products during the launch offer.",
        scope: "PRODUCT",
        productIds: firstProductId,
        categoryIds: "",
        discountType: "PERCENTAGE",
        discountValue: "20",
        minOrderAmount: "1000",
        usageLimit: "100",
        consultantScope: firstConsultantId ? "SPECIFIC_CONSULTANTS" : "NO_CONSULTANT",
        consultantIds: firstConsultantId,
        consultantShareType: "PERCENTAGE",
        consultantShareValue: "10",
        consultantShareOn: "TOTAL_PRICE",
        minConsultationMinutes: "25",
        userScope: firstUserId ? "SPECIFIC_USERS" : "ALL_USERS",
        userIds: firstUserId,
        startsAt: dateAfterDays(0),
        endsAt: dateAfterDays(14),
      },
      review: {
        ...initialForms.review,
        productId: firstProductId,
        reviewerName: "Raju Sharma",
        reviewerLocation: "Delhi",
        rating: "5",
        title: "Excellent quality",
        comment: "The product quality and packaging were very good. It felt premium and authentic.",
        isVerifiedBuyer: true,
        reviewDate: dateInputValue(new Date()),
        sortOrder: 1,
      },
    };

    setForm(testData[activeTab]);
  };

  const addFormData = () => {
    const formData = new FormData();
    const skipKeys = ["_id", "id", "__v", "createdAt", "updatedAt", "imageUrl", "imageUrls", "primaryImageUrl"];
    Object.entries(form).forEach(([key, value]) => {
      if (skipKeys.includes(key)) return;
      if (Array.isArray(value)) {
        const cleanedValue = key === "addons"
          ? value.filter((item) => item?.title || item?.price || item?.description)
          : value;
        formData.append(key, JSON.stringify(cleanedValue));
      } else if (value !== undefined && value !== null) {
        formData.append(key, DATETIME_FIELDS.includes(key) ? localDateTimeToIso(value) || value : value);
      }
    });
    if (selected?._id) formData.append("id", String(selected._id));
    if (image) formData.append("image", image);
    images.forEach((item) => formData.append("images", item));
    return formData;
  };

  const submitHandler = async () => {
    try {
      if (activeTab === "coupon" && form.consultantScope === "SPECIFIC_CONSULTANTS" && !splitIds(form.consultantIds).length) {
        toastMessage("Please select at least one consultant or choose No Consultant / Company Coupon.", "error", "ecommerceSave");
        return;
      }
      if (activeTab === "review" && (!form.productId || !form.reviewerName || !form.comment)) {
        toastMessage("Please select product, reviewer name, and review comment.", "error", "ecommerceSave");
        return;
      }
      const multipart = ["category", "product", "banner", "review"].includes(activeTab);
      const payload = multipart ? addFormData() : withIsoDateTimes({ ...form, id: selected?._id });
      if (!multipart && activeTab !== "category") {
        payload.productIds = form.productIds;
        payload.categoryIds = "";
        delete payload.maxDiscountAmount;
        if (activeTab === "offer" || activeTab === "coupon") payload.scope = "PRODUCT";
        if (activeTab === "coupon" && form.consultantScope !== "SPECIFIC_CONSULTANTS") {
          payload.consultantIds = "";
          payload.consultantShareType = "NONE";
          payload.consultantShareValue = "";
          payload.consultantShareOn = "TOTAL_PRICE";
          payload.minConsultationMinutes = 0;
        }
      }
      const { data } = selected
        ? await updateEcommerceApi(activeTab, payload, multipart)
        : await addEcommerceApi(activeTab, payload, multipart);
      if (data.code === 200) {
        toastMessage(data.message, "success", "ecommerceSave");
        setModalOpen(false);
        fetchList();
        fetchLookups();
      } else {
        toastMessage(data.message, "error", "ecommerceSave");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || error.message, "error", "ecommerceSave");
    }
  };

  const statusHandler = async (item) => {
    const { data } = await changeEcommerceStatusApi({
      type: activeTab,
      id: item._id,
      status: !item.status,
    });
    if (data.code === 200) {
      fetchList();
      fetchLookups();
    } else {
      toastMessage(data.message, "error", "ecommerceStatus");
    }
  };

  const shiprocketHandler = async (action, item) => {
    try {
      const { data } = await shiprocketEcommerceApi(action, { orderId: item._id });
      if (data.code === 200) {
        toastMessage(data.message, "success", "shiprocketAction");
        fetchList();
      } else {
        toastMessage(data.message, "error", "shiprocketAction");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || error.message, "error", "shiprocketAction");
    }
  };

  const deleteHandler = async () => {
    const { data } = await deleteEcommerceApi(activeTab, deleteTarget?._id);
    if (data.code === 200) {
      toastMessage(data.message, "success", "ecommerceDelete");
      setDeleteTarget(null);
      fetchList();
      fetchLookups();
    } else {
      toastMessage(data.message, "error", "ecommerceDelete");
    }
  };

  const openCancelOrder = (item) => {
    setCancelReason("");
    setCancelTarget(item);
  };

  const closeCancelOrder = () => {
    if (cancelLoading) return;
    setCancelTarget(null);
    setCancelReason("");
  };

  const cancelOrderHandler = async () => {
    if (!cancelTarget?._id || cancelLoading) return;
    setCancelLoading(true);
    try {
      const { data } = await cancelEcommerceOrderApi({
        orderId: cancelTarget._id,
        reason: cancelReason.trim(),
      });
      if (data.code === 200) {
        toastMessage(data.message || "Order cancelled successfully", "success", "ecommerceCancel");
        setCancelTarget(null);
        setCancelReason("");
        fetchList();
      } else {
        toastMessage(data.message || "Unable to cancel order", "error", "ecommerceCancel");
      }
    } catch (error) {
      toastMessage(error?.response?.data?.message || error.message, "error", "ecommerceCancel");
    } finally {
      setCancelLoading(false);
    }
  };

  const renderSelectOptions = (options) => (
    <>
      <option value="">Select</option>
      {options.map((item) => (
        <option key={item.value} value={item.value}>{item.label}</option>
      ))}
    </>
  );

  const renderEntitySelector = ({ value, options, onChange, searchValue, setSearchValue, placeholder, idLabel = "ID", showPublicId = true, disabled = false }) => {
    const selectedIds = splitIds(value);
    const filteredOptions = options.filter((item) => {
      const haystack = personSearchText({ ...item.raw, label: item.label, value: item.value, publicId: item.publicId });
      return haystack.includes(String(searchValue || "").toLowerCase());
    });
    const selectedOptions = options.filter((item) => selectedIds.includes(item.value));
    const toggleId = (id) => {
      const nextIds = selectedIds.includes(id)
        ? selectedIds.filter((item) => item !== id)
        : [...selectedIds, id];
      onChange(nextIds.join(","));
    };

    return (
      <div style={styles.selectorBox}>
        <input
          style={{ ...styles.selectorSearch, background: disabled ? "#f7f7f7" : "#fff" }}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
        <div style={styles.selectorTableWrap}>
          <table style={styles.selectorTable}>
            <thead style={styles.selectorHead}>
              <tr>
                <th style={{ ...styles.selectorCell, width: 56 }}>Pick</th>
                <th style={{ ...styles.selectorCell, width: showPublicId ? "30%" : "42%" }}>Name</th>
                {showPublicId && <th style={{ ...styles.selectorCell, width: "20%" }}>{idLabel}</th>}
                <th style={styles.selectorCell}>Backend ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredOptions.slice(0, 50).map((item) => {
                const checked = selectedIds.includes(item.value);
                return (
                  <tr key={item.value} style={{ background: checked ? ADMIN_PRIMARY_SOFT : "#fff" }}>
                    <td style={styles.selectorCell}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleId(item.value)}
                        disabled={disabled}
                      />
                    </td>
                    <td style={styles.selectorCell}>
                      <b>{item.label}</b>
                    </td>
                    {showPublicId && (
                      <td style={styles.selectorCell}>
                        <b style={{ color: "#333" }}>{item.publicId || "---"}</b>
                      </td>
                    )}
                    <td style={styles.selectorCell}>
                      <span style={styles.selectorId}>ID: {item.value}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filteredOptions.length && <div style={styles.selectorEmpty}>No matching record found. Type at least 2 characters to fetch from server.</div>}
        </div>
        <div style={styles.selectorSummary}>
          {disabled
            ? "Selection is not required for All scope."
            : `Selected ${selectedIds.length}: ${selectedOptions.length ? selectedOptions.map((item) => `${item.label}${item.publicId ? ` - ${item.publicId}` : ""} (${item.value})`).join(", ") : "none"}`}
        </div>
      </div>
    );
  };

  const selectedNames = (value, options) => {
    const ids = splitIds(value);
    return options.filter((item) => ids.includes(item.value)).map((item) => item.label);
  };

  const namesFromRecords = (items = []) => {
    if (!Array.isArray(items) || !items.length) return "";
    return items.map((item) => item?.title || personLabel(item) || item?._id || item).filter(Boolean).join(", ");
  };

  const fullJson = (value) => {
    if (!value || !Object.keys(value || {}).length) return "---";
    return JSON.stringify(value, null, 2);
  };

  const renderDetailLine = (label, value) => (
    <div style={styles.detailText}>
      <b style={{ color: "#111" }}>{label}:</b> <span>{value === 0 || value ? value : "---"}</span>
    </div>
  );

  const canCancelEcommerceOrder = (item = {}) => {
    const orderStatus = String(item.orderStatus || "PLACED").toUpperCase();
    const shippingStatus = String(item.shippingStatus || "").toUpperCase();
    if (BLOCKED_CANCEL_ORDER_STATUSES.includes(orderStatus)) return false;
    if (BLOCKED_CANCEL_SHIPPING_STATUSES.includes(shippingStatus)) return false;
    return CANCELLABLE_ORDER_STATUSES.includes(orderStatus);
  };

  const recordId = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    return value._id || value.id || "";
  };

  const renderTrackingEvent = (event) => (
    <div key={event._id} style={styles.trackingEvent}>
      <div>
        <b>{formatDateTime(event.createdAt)}</b>
        {renderDetailLine("Event", event.eventType)}
        {renderDetailLine("Screen", event.screen)}
      </div>
      <div>
        {renderDetailLine("Product", event.productId?.title || recordId(event.productId))}
        {event.productId?.price !== undefined && renderDetailLine("Product Price", `Rs ${event.productId.price}`)}
        {renderDetailLine("Cart Item ID", event.cartItemId)}
        {renderDetailLine("Order", event.orderId?.orderNo || recordId(event.orderId))}
        {event.orderId?.total !== undefined && renderDetailLine("Order Total", `Rs ${event.orderId.total}`)}
        {event.orderId?.orderStatus && renderDetailLine("Order Status", event.orderId.orderStatus)}
      </div>
      <div>
        {renderDetailLine("Platform", event.platform)}
        {renderDetailLine("Device ID", event.deviceId)}
        <pre style={{ ...styles.jsonBlock, maxWidth: "100%" }}>{fullJson(event.metadata)}</pre>
      </div>
    </div>
  );

  const renderTrackingRows = () => {
    if (!list.length) {
      return <NotFound cols={7} msg="No ecommerce tracking records found!" icon={NOT_FOUND} loader={loader} />;
    }

    return list.map((item, index) => {
      const latestEvent = item.latestEvent || {};
      const expanded = Boolean(trackingExpanded[item._id]);
      return (
        <React.Fragment key={item._id}>
          <tr>
            <td>{findSerialNumber(index, activePage, limit)}</td>
            <td>
              <b>{personLabel(item.userId) || "Unknown User"}</b>
              {renderDetailLine("User ID", recordId(item.userId))}
              {renderDetailLine("Total Events", item.totalEvents)}
              {renderDetailLine("First Seen", formatDateTime(item.firstActivityAt))}
            </td>
            <td>
              <b>{latestEvent.productId?.title || latestEvent.orderId?.orderNo || "---"}</b>
              {renderDetailLine("Latest Event", item.latestEventType)}
              {renderDetailLine("Latest Screen", item.latestScreen)}
              {renderDetailLine("Product ID", recordId(latestEvent.productId))}
              {renderDetailLine("Order", latestEvent.orderId?.orderNo || recordId(latestEvent.orderId))}
            </td>
            <td>
              <pre style={styles.jsonBlock}>{fullJson(latestEvent.metadata)}</pre>
            </td>
            <td>
              <b>{item.platform || latestEvent.platform || "---"}</b>
              {renderDetailLine("Device ID", item.deviceId || latestEvent.deviceId)}
              {renderDetailLine("Last Activity", formatDateTime(item.latestActivityAt))}
            </td>
            <td><span style={styles.badge}>{item.latestEventType || "Tracked"}</span></td>
            <td>
              <button
                type="button"
                style={styles.linkButton}
                onClick={() => setTrackingExpanded((prev) => ({ ...prev, [item._id]: !prev[item._id] }))}
              >
                {expanded ? "Hide History" : "View History"}
              </button>
            </td>
          </tr>
          {expanded && (
            <tr>
              <td colSpan={7}>
                <div style={styles.trackingHistory}>
                  <b>Complete Ecommerce History</b>
                  {(item.events || []).map(renderTrackingEvent)}
                  {!item.events?.length && <div style={styles.detailText}>No history found.</div>}
                </div>
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  const formatRupees = (value) => `Rs ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

  const orderItemAddonsTotal = (orderItem = {}) =>
    (orderItem.addons || []).reduce((sum, addon) => sum + Number(addon.price || 0), 0);

  const getOrderItemPricing = (orderItem = {}) => {
    const quantity = Number(orderItem.quantity || 1);
    const addonsTotal = orderItemAddonsTotal(orderItem);
    const normalUnitPrice = Number(orderItem.normalPrice || orderItem.price || 0);
    const afterOfferUnitPrice = Number(orderItem.finalPrice || orderItem.price || 0);
    const marketUnitPrice = Number(orderItem.marketPrice || orderItem.productId?.compareAtPrice || orderItem.productId?.price || orderItem.price || 0);
    const marketTotal = Number(orderItem.marketLineTotal || (marketUnitPrice + addonsTotal) * quantity);
    const normalTotal = Number(orderItem.normalLineTotal || (normalUnitPrice + addonsTotal) * quantity);
    const offerDiscount = Number(orderItem.offerDiscountAmount || Math.max(0, normalUnitPrice - afterOfferUnitPrice) * quantity);
    const afterOfferTotal = Number(orderItem.lineTotal || (afterOfferUnitPrice + addonsTotal) * quantity);
    const couponDiscount = Number(orderItem.couponDiscountAmount || 0);
    const finalTotal = Number(orderItem.linePayable || Math.max(0, afterOfferTotal - couponDiscount));
    return {
      quantity,
      addonsTotal,
      marketUnitPrice,
      normalUnitPrice,
      afterOfferUnitPrice,
      marketTotal,
      normalTotal,
      offerDiscount,
      afterOfferTotal,
      couponDiscount,
      finalTotal,
      totalSaved: Number(orderItem.totalSaved || Math.max(0, marketTotal - finalTotal)),
    };
  };

  const getOrderPriceSummary = (order = {}) => {
    const itemPricing = (order.items || []).map(getOrderItemPricing);
    const marketTotal = Number(order.marketSubtotal || itemPricing.reduce((sum, item) => sum + item.marketTotal, 0));
    const normalTotal = Number(order.normalSubtotal || itemPricing.reduce((sum, item) => sum + item.normalTotal, 0));
    const offerDiscount = Number(order.offerDiscountAmount || itemPricing.reduce((sum, item) => sum + item.offerDiscount, 0));
    const afterOfferTotal = Number(order.withoutCouponTotal || order.subtotal || itemPricing.reduce((sum, item) => sum + item.afterOfferTotal, 0));
    const couponDiscount = Number(order.couponDiscountAmount || order.discountAmount || 0);
    const finalTotal = Number(order.total || Math.max(0, afterOfferTotal - couponDiscount));
    return {
      marketTotal,
      normalTotal,
      offerDiscount,
      afterOfferTotal,
      couponDiscount,
      finalTotal,
      totalSaved: Number(order.totalSaved || Math.max(0, marketTotal - finalTotal)),
    };
  };

  const getOrderConsultantInfo = (order = {}, pricing = {}) => {
    const fallbackConsultant = order.couponId?.consultantScope === "SPECIFIC_CONSULTANTS"
      ? order.couponId?.consultantIds?.[0]
      : null;
    const consultant = order.consultantId || fallbackConsultant;
    let shareAmount = Number(order.consultantShareAmount || 0);
    if (!shareAmount && fallbackConsultant && order.couponId?.consultantShareType && order.couponId.consultantShareType !== "NONE") {
      const shareBase = order.couponId.consultantShareOn === "DISCOUNT_AMOUNT"
        ? Number(pricing.couponDiscount || 0)
        : Number(pricing.afterOfferTotal || 0);
      shareAmount = order.couponId.consultantShareType === "PERCENTAGE"
        ? Math.round((shareBase * Number(order.couponId.consultantShareValue || 0)) / 100)
        : Number(order.couponId.consultantShareValue || 0);
    }
    return { consultant, shareAmount };
  };

  const getCouponUsageInfo = (coupon = {}) => {
    const usedCount = Number(coupon.usedCount || 0);
    const reservedCount = Number(coupon.reservedCount || 0);
    const usageLimit = Number(coupon.usageLimit || 0);
    return {
      usedCount,
      reservedCount,
      usageLimit,
      remaining: usageLimit > 0 ? Math.max(0, usageLimit - usedCount - reservedCount) : null,
    };
  };

  const renderOrderItems = (items = []) => {
    if (!items.length) return <div style={styles.detailText}>No items found.</div>;
    return (
      <div style={styles.orderItems}>
        {items.map((orderItem, itemIndex) => {
          const pricing = getOrderItemPricing(orderItem);
          return (
            <div key={`${recordId(orderItem.productId) || orderItem.title}-${itemIndex}`} style={styles.orderItem}>
              {orderItem.imageUrl ? (
                <img src={orderItem.imageUrl} alt={orderItem.title || "Order item"} style={styles.orderItemImage} />
              ) : (
                <div style={{ ...styles.orderItemImage, display: "grid", placeItems: "center", color: ADMIN_PRIMARY_DARK, fontWeight: 800 }}>---</div>
              )}
              <div>
                <b>{orderItem.title || "---"}</b>
                {renderDetailLine("Product ID", recordId(orderItem.productId))}
                {renderDetailLine("Qty", orderItem.quantity || 1)}
                {renderDetailLine("Market Price", formatRupees(pricing.marketUnitPrice))}
                {renderDetailLine("Normal Price", formatRupees(pricing.normalUnitPrice))}
                {pricing.offerDiscount > 0 && renderDetailLine("Offer Discount", formatRupees(pricing.offerDiscount))}
                {renderDetailLine("Price After Offer", formatRupees(pricing.afterOfferUnitPrice))}
                {renderDetailLine("Line Total After Offer", formatRupees(orderItem.lineTotal || pricing.afterOfferTotal))}
                {pricing.couponDiscount > 0 && renderDetailLine("Coupon Discount", formatRupees(pricing.couponDiscount))}
                {pricing.couponDiscount > 0 && renderDetailLine("Final Line Price", formatRupees(pricing.finalTotal))}
                {pricing.totalSaved > 0 && renderDetailLine("Line Saved", formatRupees(pricing.totalSaved))}
                {orderItem.offer?.title && renderDetailLine("Offer Used", orderItem.offer.title)}
                {!!orderItem.addons?.length && (
                  <div>
                    {orderItem.addons.map((addon, addonIndex) => (
                      <span key={`${addon.title}-${addonIndex}`} style={styles.orderAddon}>
                        {addon.title || "Addon"}: {formatRupees(addon.price)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderShipping = (shipping = {}, user = {}) => (
    <>
      {renderDetailLine("Name", shipping.fullName)}
      {renderDetailLine("Phone", shipping.phone || userPhoneNumber(user))}
      {renderDetailLine("Address", shipping.address)}
      {renderDetailLine("City", shipping.city)}
      {renderDetailLine("Pincode", shipping.pincode)}
    </>
  );

  const renderShipmentDetails = (item = {}) => (
    <div style={styles.shipmentBox}>
      {renderDetailLine("Shiprocket", item.shippingStatus || "Not created")}
      {renderDetailLine("Shipment ID", item.shiprocketShipmentId)}
      {renderDetailLine("AWB", item.awbCode)}
      {renderDetailLine("Courier", item.courierName)}
      {renderDetailLine("Expected", formatDateTime(item.estimatedDeliveryDate))}
      {String(item.orderStatus || "").toUpperCase() === "CANCELLED" && renderDetailLine("Cancelled By", item.cancelSource || "ADMIN")}
      {!!item.cancelReason && renderDetailLine("Cancel Reason", item.cancelReason)}
      {!!item.trackingUrl && (
        <a href={item.trackingUrl} target="_blank" rel="noreferrer" style={styles.shiprocketLink}>Open Tracking</a>
      )}
    </div>
  );

  const renderShiprocketActions = (item = {}) => {
    const cancelled = String(item.orderStatus || "").toUpperCase() === "CANCELLED";
    return (
      <div style={styles.shiprocketActions}>
        {!cancelled && !item.shiprocketShipmentId && (
          <button type="button" style={{ ...styles.shiprocketButton, ...styles.shiprocketButtonPrimary }} onClick={() => shiprocketHandler("create", item)}>
            Create Shipment
          </button>
        )}
        {!cancelled && !!item.shiprocketShipmentId && !item.awbCode && (
          <button type="button" style={styles.shiprocketButton} onClick={() => shiprocketHandler("assign-awb", item)}>
            Assign AWB
          </button>
        )}
        {!cancelled && !!item.shiprocketShipmentId && (
          <button type="button" style={styles.shiprocketButton} onClick={() => shiprocketHandler("pickup", item)}>
            Pickup
          </button>
        )}
        {!!(item.awbCode || item.shiprocketShipmentId || item.shiprocketOrderId) && (
          <button type="button" style={styles.shiprocketButton} onClick={() => shiprocketHandler("sync", item)}>
            Sync Tracking
          </button>
        )}
        {!!item.trackingUrl && (
          <a href={item.trackingUrl} target="_blank" rel="noreferrer" style={styles.shiprocketLink}>Track</a>
        )}
        {canCancelEcommerceOrder(item) && (
          <button type="button" style={styles.shiprocketButtonDanger} onClick={() => openCancelOrder(item)}>
            Cancel Order
          </button>
        )}
      </div>
    );
  };

  const renderOrderRows = () => {
    if (!list.length) {
      return <NotFound cols={8} msg="No ecommerce orders found!" icon={NOT_FOUND} loader={loader} />;
    }
    return list.map((item, index) => {
      const pricing = getOrderPriceSummary(item);
      const consultantInfo = getOrderConsultantInfo(item, pricing);
      return (
        <tr key={item._id}>
          <td>{findSerialNumber(index, activePage, limit)}</td>
          <td>
            <b>{item.orderNo || "---"}</b>
            {renderDetailLine("Order ID", item._id)}
            {renderDetailLine("User", personLabel(item.userId))}
            {renderDetailLine("User ID", recordId(item.userId))}
            {renderDetailLine("Created", formatDateTime(item.createdAt))}
          </td>
          <td>{renderOrderItems(item.items || [])}</td>
          <td>
            {renderDetailLine("Market Price", formatRupees(pricing.marketTotal))}
            {renderDetailLine("Normal Price", formatRupees(pricing.normalTotal))}
            {pricing.offerDiscount > 0 && renderDetailLine("Offer Discount", formatRupees(pricing.offerDiscount))}
            {renderDetailLine("Price After Offer", formatRupees(pricing.afterOfferTotal))}
            {renderDetailLine("Coupon Discount", formatRupees(pricing.couponDiscount))}
            {renderDetailLine("Coupon", item.couponCode || item.couponSnapshot?.code || item.couponId?.code)}
            {(item.couponSnapshot?.title || item.couponId?.title) && renderDetailLine("Coupon Title", item.couponSnapshot?.title || item.couponId.title)}
            <div style={styles.orderFinalPrice}>
              Final Price After Coupon: {formatRupees(pricing.finalTotal)}
            </div>
            {pricing.totalSaved > 0 && renderDetailLine("Total Saved", formatRupees(pricing.totalSaved))}
            {renderDetailLine("Consultant", consultantInfo.consultant ? personLabel(consultantInfo.consultant) : "No Consultant")}
            {renderDetailLine("Consultant Share", formatRupees(consultantInfo.shareAmount))}
            {renderDetailLine("Share Credited", item.consultantShareCredited ? "Yes" : "No")}
          </td>
          <td>
            {renderShipping(item.shipping, item.userId)}
            {renderShipmentDetails(item)}
          </td>
          <td>
            <b>{item.paymentMethod || "---"}</b>
            {renderDetailLine("Payment", item.paymentStatus)}
            {renderDetailLine("Updated", formatDateTime(item.updatedAt))}
          </td>
          <td><span style={styles.badge}>{item.orderStatus || "---"}</span></td>
          <td>{renderShiprocketActions(item)}</td>
        </tr>
      );
    });
  };

  const getRecordId = (item) => String(item?._id || item || "");

  const recordMatchesProduct = (record, product) => {
    const productId = getRecordId(product);
    if (!productId || record?.scope !== "PRODUCT") return false;
    return (record.productIds || []).some((item) => getRecordId(item) === productId);
  };

  const discountText = (item) => `${item.discountValue || 0}${item.discountType === "PERCENTAGE" ? "% OFF" : " Rs OFF"}`;

  const getDiscountAmount = (item, price) => {
    const discountValue = Number(item?.discountValue || 0);
    if (!price || !discountValue) return 0;

    const discountAmount = item.discountType === "PERCENTAGE"
      ? (price * discountValue) / 100
      : discountValue;
    return Math.min(price, discountAmount);
  };

  const getPlatformPriceRows = (product) => {
    const defaultPrice = Number(product?.price || 0);
    const defaultMrp = Number(product?.compareAtPrice || 0);
    const platformConfigs = [
      { key: "default", label: "Default", price: defaultPrice, mrp: defaultMrp, alwaysShow: true },
      { key: "android", label: "Android", price: Number(product?.androidPrice || 0), mrp: Number(product?.androidCompareAtPrice || 0) },
      { key: "ios", label: "iOS", price: Number(product?.iosPrice || 0), mrp: Number(product?.iosCompareAtPrice || 0) },
    ];

    return platformConfigs
      .filter((item) => item.alwaysShow || item.price > 0 || item.mrp > 0)
      .map((item) => {
        const price = item.price > 0 ? item.price : defaultPrice;
        const mrp = item.mrp > 0 ? item.mrp : defaultMrp;
        return {
          ...item,
          price,
          mrp,
          isFallbackPrice: !item.alwaysShow && item.price <= 0,
          isFallbackMrp: !item.alwaysShow && item.mrp <= 0,
        };
      });
  };

  const getBestDiscountResult = (items, price) => {
    if (!items.length || !price) return null;
    return items.reduce((best, item) => {
      const discountAmount = getDiscountAmount(item, price);
      if (!discountAmount) return best;
      const result = {
        item,
        discountAmount,
        finalPrice: Math.max(0, price - discountAmount),
      };
      if (!best || result.finalPrice < best.finalPrice) return result;
      return best;
    }, null);
  };

  const getBestComboResult = (offersList, couponsList, price) => {
    if (!offersList.length || !couponsList.length || !price) return null;
    let best = null;
    offersList.forEach((offer) => {
      const offerDiscount = getDiscountAmount(offer, price);
      const offerPrice = Math.max(0, price - offerDiscount);
      couponsList.forEach((coupon) => {
        const couponDiscount = getDiscountAmount(coupon, offerPrice);
        const finalPrice = Math.max(0, offerPrice - couponDiscount);
        const result = {
          offer,
          coupon,
          offerDiscount,
          couponDiscount,
          finalPrice,
          saved: price - finalPrice,
        };
        if (!best || result.finalPrice < best.finalPrice) best = result;
      });
    });
    return best;
  };

  const calculateDiscountPricing = (item, product) => {
    const price = Number(product?.price || 0);
    const discountAmount = getDiscountAmount(item, price);
    if (!price || !discountAmount) return null;

    return {
      price,
      discountAmount,
      finalPrice: Math.max(0, price - discountAmount),
    };
  };

  const renderDiscountPricePreview = (product, discountItem) => {
    const pricing = calculateDiscountPricing(discountItem, product);
    if (!product?._id) {
      return (
        <div style={{ ...styles.meaningNote, ...styles.full, marginBottom: 0 }}>
          Select product to see final customer price.
        </div>
      );
    }
    if (!pricing) {
      return (
        <div style={{ ...styles.meaningNote, ...styles.full, marginBottom: 0 }}>
          Product price: {formatRupees(product.price)}. Enter discount value to see final customer price.
        </div>
      );
    }

    return (
      <div style={{ ...styles.meaningNote, ...styles.full, marginBottom: 0 }}>
        <b>{product.title || "Selected Product"}</b>
        {Number(product.compareAtPrice || 0) > Number(product.price || 0) && (
          <div>Market price: {formatRupees(product.compareAtPrice)}</div>
        )}
        <div>Without offer/coupon: {formatRupees(pricing.price)}</div>
        <div>Discount: {formatRupees(pricing.discountAmount)}</div>
        <div><b>Final customer price: {formatRupees(pricing.finalPrice)}</b></div>
      </div>
    );
  };

  const renderProductPriceMatrix = (product, productOffers, productCoupons) => {
    const rows = getPlatformPriceRows(product);
    const bestOverall = rows.reduce((best, row) => {
      const offerResult = getBestDiscountResult(productOffers, row.price);
      const couponResult = getBestDiscountResult(productCoupons, row.price);
      const comboResult = getBestComboResult(productOffers, productCoupons, row.price);
      const finalPrice = comboResult?.finalPrice ?? offerResult?.finalPrice ?? couponResult?.finalPrice ?? row.price;
      const result = { platform: row.label, finalPrice };
      if (!best || result.finalPrice < best.finalPrice) return result;
      return best;
    }, null);
    const headers = ["Platform", "MRP", "Base", "Only Offer", "Only Coupon", "Both Apply", "Final"];

    const renderMoneyCell = (value, muted, isFinal = false) => (
      <div style={{ ...styles.priceLedgerCell, ...(isFinal ? styles.priceLedgerFinal : {}) }}>
        <span style={isFinal ? undefined : styles.priceLedgerValue}>{value}</span>
        {muted && <span style={styles.priceLedgerMuted}>{muted}</span>}
      </div>
    );

    return (
      <div style={styles.priceLedger}>
        <div style={styles.priceLedgerTitle}>
          <div>
            <div style={styles.priceLedgerTitleText}>Pricing Breakdown</div>
            {bestOverall && (
              <div style={styles.priceLedgerMuted}>
                Best final: {formatRupees(bestOverall.finalPrice)} on {bestOverall.platform}
              </div>
            )}
          </div>
          <div style={styles.priceLedgerGst}>GST {product.gstPercent || 0}%</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <div style={styles.priceLedgerGrid}>
            {headers.map((header, index) => (
              <div key={header} style={{ ...styles.priceLedgerHead, ...(index === headers.length - 1 ? { borderRight: 0 } : {}) }}>
                {header}
              </div>
            ))}
            {rows.map((row) => {
              const offerResult = getBestDiscountResult(productOffers, row.price);
              const couponResult = getBestDiscountResult(productCoupons, row.price);
              const comboResult = getBestComboResult(productOffers, productCoupons, row.price);
              const finalPrice = comboResult?.finalPrice ?? offerResult?.finalPrice ?? couponResult?.finalPrice ?? row.price;
              const finalSaved = Math.max(0, row.price - finalPrice);
              const marketSaved = Math.max(0, row.mrp - finalPrice);

              return (
                <React.Fragment key={row.key}>
                  <div style={{ ...styles.priceLedgerCell, ...styles.priceLedgerPlatform }}>
                    <span>{row.label}</span>
                    {row.isFallbackPrice && <span style={styles.priceLedgerMuted}>Default price</span>}
                    {row.isFallbackMrp && row.mrp > 0 && <span style={styles.priceLedgerMuted}>Default MRP</span>}
                  </div>
                  {renderMoneyCell(row.mrp > row.price ? formatRupees(row.mrp) : "---")}
                  {renderMoneyCell(formatRupees(row.price))}
                  {renderMoneyCell(
                    offerResult ? formatRupees(offerResult.finalPrice) : "---",
                    offerResult ? `Save ${formatRupees(offerResult.discountAmount)}` : ""
                  )}
                  {renderMoneyCell(
                    couponResult ? formatRupees(couponResult.finalPrice) : "---",
                    couponResult ? `Save ${formatRupees(couponResult.discountAmount)}` : ""
                  )}
                  {renderMoneyCell(
                    comboResult ? formatRupees(comboResult.finalPrice) : "---",
                    comboResult ? `Save ${formatRupees(comboResult.saved)}` : ""
                  )}
                  {renderMoneyCell(
                    formatRupees(finalPrice),
                    [
                      finalSaved > 0 ? `Save ${formatRupees(finalSaved)}` : "",
                      marketSaved > finalSaved ? `MRP save ${formatRupees(marketSaved)}` : "",
                    ].filter(Boolean).join(" | "),
                    true
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
        <div style={styles.priceRuleStrip}>
          {!productOffers.length && !productCoupons.length && (
            <span style={styles.priceRuleChip}>No active offer or coupon mapped</span>
          )}
          {productOffers.map((item) => (
            <span key={`rule-offer-${item._id}`} style={styles.priceRuleChip}>
              <b>Offer:</b> {item.title || "---"} ({discountText(item)}) · {getTimeLeft(item.endsAt, nowTick)}
            </span>
          ))}
          {productCoupons.map((item) => (
            <span key={`rule-coupon-${item._id}`} style={styles.priceRuleChip}>
              <b>Coupon:</b> {item.code || item.title || "---"} ({discountText(item)}) · {getTimeLeft(item.endsAt, nowTick)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderProductDiscounts = (product) => {
    const productOffers = offers.filter((item) => recordMatchesProduct(item, product));
    const productCoupons = coupons.filter((item) => recordMatchesProduct(item, product));

    if (!productOffers.length && !productCoupons.length) {
      return renderProductPriceMatrix(product, productOffers, productCoupons);
    }

    return renderProductPriceMatrix(product, productOffers, productCoupons);
  };

  const renderForm = () => {
    if (activeTab === "category") {
      return (
        <div style={styles.formGrid}>
          <Field label="Title" help="Category name. This appears on the category chip or card in the app.">
            <input style={styles.input} value={form.title} onChange={(e) => updateForm("title", e.target.value)} />
          </Field>
          <Field label="Icon" help="Short text or icon code for the category tile, for example RD, GM, or BR. Optional if you upload an image.">
            <input style={styles.input} value={form.icon} onChange={(e) => updateForm("icon", e.target.value)} />
          </Field>
          <Field label="Sort Order" help="Controls category ordering. Lower numbers appear first.">
            <input type="number" style={styles.input} value={form.sortOrder} onChange={(e) => updateForm("sortOrder", e.target.value)} />
          </Field>
          <Field label="Image" help="Category image or icon. Optional.">
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
          </Field>
          <Field label="Description" full help="Short category detail. This can be used as a subtitle or detail text in the app.">
            <textarea style={styles.textarea} value={form.description} onChange={(e) => updateForm("description", e.target.value)} />
          </Field>
        </div>
      );
    }

    if (activeTab === "product") {
      return (
        <div style={styles.formGrid}>
          <Field label="Title" help="Product name. This appears on the product card and detail screen.">
            <input style={styles.input} value={form.title} onChange={(e) => updateForm("title", e.target.value)} />
          </Field>
          <Field label="Category" help="Select where this product should appear, such as Rudraksha or Gemstone.">
            <select style={styles.input} value={form.categoryId} onChange={(e) => updateForm("categoryId", e.target.value)}>{renderSelectOptions(categoryOptions)}</select>
          </Field>
          <Field label="Default Price" help="Fallback selling price. Used for both Android and iOS when platform-specific price is blank.">
            <input type="number" style={styles.input} value={form.price} onChange={(e) => updateForm("price", e.target.value)} />
          </Field>
          <Field label="Default MRP / Compare Price" help="Fallback old price or MRP. Used when Android/iOS MRP is blank.">
            <input type="number" style={styles.input} value={form.compareAtPrice} onChange={(e) => updateForm("compareAtPrice", e.target.value)} />
          </Field>
          <Field label="Android Price" help="Optional Android selling price. Leave blank or 0 to use Default Price.">
            <input type="number" style={styles.input} value={form.androidPrice} onChange={(e) => updateForm("androidPrice", e.target.value)} />
          </Field>
          <Field label="Android MRP / Compare Price" help="Optional Android old price or MRP. Leave blank or 0 to use Default MRP.">
            <input type="number" style={styles.input} value={form.androidCompareAtPrice} onChange={(e) => updateForm("androidCompareAtPrice", e.target.value)} />
          </Field>
          <Field label="iOS Price" help="Optional iOS selling price. Leave blank or 0 to use Default Price.">
            <input type="number" style={styles.input} value={form.iosPrice} onChange={(e) => updateForm("iosPrice", e.target.value)} />
          </Field>
          <Field label="iOS MRP / Compare Price" help="Optional iOS old price or MRP. Leave blank or 0 to use Default MRP.">
            <input type="number" style={styles.input} value={form.iosCompareAtPrice} onChange={(e) => updateForm("iosCompareAtPrice", e.target.value)} />
          </Field>
          <Field label="GST Percent" help="GST percentage applied on this product after offer and addons. Select 0 if no GST should be added.">
            <select style={styles.input} value={form.gstPercent} onChange={(e) => updateForm("gstPercent", e.target.value)}>
              <option value="">0%</option>
              <option value="0">0%</option>
              <option value="3">3%</option>
              <option value="5">5%</option>
              <option value="12">12%</option>
              <option value="18">18%</option>
              <option value="28">28%</option>
            </select>
          </Field>
          <Field label="SKU" help="Internal product code for admin or inventory tracking. It is usually not shown to users.">
            <input style={styles.input} value={form.sku} onChange={(e) => updateForm("sku", e.target.value)} />
          </Field>
          <Field label="Stock" help="Available quantity. If it is 0, the app can treat the product as out of stock.">
            <input type="number" style={styles.input} value={form.stock} onChange={(e) => updateForm("stock", e.target.value)} />
          </Field>
          <Field label="Tags" help="Search and filter keywords. Separate multiple tags with commas, for example rudraksha,mala,spiritual.">
            <input style={styles.input} value={form.tags} onChange={(e) => updateForm("tags", e.target.value)} />
          </Field>
          <Field label="Images" help="Select one or more product photos. The first image becomes the primary listing image; all images appear on the product detail screen.">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                addManagedImages(e.target.files);
                e.target.value = "";
              }}
            />
            <div style={styles.imageList}>
              {existingManagedImages.map((item, index) => (
                <div style={styles.imageItem} key={`existing-${item.key}-${index}`}>
                  {item.url ? <img src={item.url} alt="Existing product" style={styles.imageThumb} /> : <div style={styles.imageThumb} />}
                  <div style={styles.imageName}>
                    <b style={styles.imageTitle}>{index === 0 ? "Primary image" : `Saved image ${index + 1}`}</b>
                    <div style={styles.imageFileName}>{item.key}</div>
                  </div>
                  <button type="button" style={styles.imageRemove} onClick={() => removeExistingManagedImage(item.key)}>Remove</button>
                </div>
              ))}
              {selectedImagePreviews.map((item, index) => (
                <div style={styles.imageItem} key={`new-${item.file.name}-${item.file.lastModified}-${index}`}>
                  <img src={item.url} alt={item.file.name} style={styles.imageThumb} />
                  <div style={styles.imageName}>
                    <b style={styles.imageTitle}>{existingManagedImages.length + index === 0 ? "Primary image" : `New image ${index + 1}`}</b>
                    <div style={styles.imageFileName}>{item.file.name}</div>
                  </div>
                  <button type="button" style={styles.imageRemove} onClick={() => removeNewManagedImage(index)}>Remove</button>
                </div>
              ))}
            </div>
            {!existingManagedImages.length && !selectedImagePreviews.length && (
              <small style={styles.help}>No product images selected yet.</small>
            )}
          </Field>
          <Field label="Product Visibility" help="Choose whether this product appears for every user or only for selected users.">
            <select
              style={styles.input}
              value={form.userScope}
              onChange={(e) => updateForm("userScope", e.target.value)}
            >
              <option value="ALL_USERS">All Users</option>
              <option value="SPECIFIC_USERS">Specific Users</option>
            </select>
          </Field>
          <Field
            label="Users Who Can See This Product"
            full
            help="Select users only when Product Visibility is Specific Users. The product will not appear for other users in the app."
          >
            {renderEntitySelector({
              value: form.userIds,
              options: userOptions,
              onChange: (value) => updateForm("userIds", value),
              searchValue: userSearch,
              setSearchValue: setUserSearch,
              placeholder: "Search user by name, phone, email, user ID, or backend ID",
              idLabel: "User ID",
              disabled: form.userScope !== "SPECIFIC_USERS",
            })}
          </Field>
          <Field label="Short Description" full help="Short line shown on listing or detail screens, usually a quick product benefit.">
            <textarea style={styles.textarea} value={form.shortDescription} onChange={(e) => updateForm("shortDescription", e.target.value)} />
          </Field>
          <Field label="Description" full help="Full product detail for the product detail screen.">
            <textarea style={styles.textarea} value={form.description} onChange={(e) => updateForm("description", e.target.value)} />
          </Field>
          <Field label="Custom Add-ons" full help="These options will show on product detail screen, like Vedic Energization or Certificate.">
            {(form.addons || []).map((addon, index) => (
              <div style={styles.addonRow} key={`${index}-${addon.title}`}>
                <input
                  style={styles.input}
                  placeholder="Addon name"
                  value={addon.title || ""}
                  onChange={(e) => updateAddon(index, "title", e.target.value)}
                />
                <input
                  type="number"
                  style={styles.input}
                  placeholder="Price"
                  value={addon.price || ""}
                  onChange={(e) => updateAddon(index, "price", e.target.value)}
                />
                <button type="button" style={{ ...styles.previewButton, padding: "9px 0" }} onClick={() => removeAddon(index)}>X</button>
              </div>
            ))}
            <button type="button" style={{ ...styles.tab, marginTop: 4 }} onClick={addAddon}>
              + Add Option
            </button>
          </Field>
        </div>
      );
    }

    if (activeTab === "review") {
      return (
        <div style={styles.formGrid}>
          <Field label="Product" help="Select the product where this review should appear.">
            <select style={styles.input} value={form.productId} onChange={(e) => updateForm("productId", e.target.value)}>{renderSelectOptions(productOptions)}</select>
          </Field>
          <Field label="Rating" help="Customer star rating from 1 to 5.">
            <select style={styles.input} value={form.rating} onChange={(e) => updateForm("rating", e.target.value)}>
              <option value="5">5 - Excellent</option>
              <option value="4">4 - Very Good</option>
              <option value="3">3 - Good</option>
              <option value="2">2 - Average</option>
              <option value="1">1 - Poor</option>
            </select>
          </Field>
          <Field label="Reviewer Name" help="Name shown with the review.">
            <input style={styles.input} value={form.reviewerName} onChange={(e) => updateForm("reviewerName", e.target.value)} />
          </Field>
          <Field label="Reviewer Location" help="Optional city/location shown below the reviewer's name.">
            <input style={styles.input} value={form.reviewerLocation} onChange={(e) => updateForm("reviewerLocation", e.target.value)} />
          </Field>
          <Field label="Review Title" help="Short headline, like Great quality or Worth buying.">
            <input style={styles.input} value={form.title} onChange={(e) => updateForm("title", e.target.value)} />
          </Field>
          <Field label="Review Date" help="Date shown on the review card.">
            <input type="datetime-local" style={styles.input} value={form.reviewDate} onChange={(e) => updateForm("reviewDate", e.target.value)} />
          </Field>
          <Field label="Sort Order" help="Lower numbers show first when multiple reviews exist.">
            <input type="number" style={styles.input} value={form.sortOrder} onChange={(e) => updateForm("sortOrder", e.target.value)} />
          </Field>
          <Field label="Verified Buyer" help="Show this review as a verified buyer review.">
            <select style={styles.input} value={String(form.isVerifiedBuyer)} onChange={(e) => updateForm("isVerifiedBuyer", e.target.value === "true")}>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </Field>
          <Field label="Review Photos" full help="Optional customer review photos. They will show inside the review card on product detail.">
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => {
                addManagedImages(e.target.files);
                e.target.value = "";
              }}
            />
            <div style={styles.imageList}>
              {existingManagedImages.map((item, index) => (
                <div style={styles.imageItem} key={`review-existing-${item.key}-${index}`}>
                  {item.url ? <img src={item.url} alt="Existing review" style={styles.imageThumb} /> : <div style={styles.imageThumb} />}
                  <div style={styles.imageName}>
                    <b style={styles.imageTitle}>{`Saved photo ${index + 1}`}</b>
                    <div style={styles.imageFileName}>{item.key}</div>
                  </div>
                  <button type="button" style={styles.imageRemove} onClick={() => removeExistingManagedImage(item.key)}>Remove</button>
                </div>
              ))}
              {selectedImagePreviews.map((item, index) => (
                <div style={styles.imageItem} key={`review-new-${item.file.name}-${item.file.lastModified}-${index}`}>
                  <img src={item.url} alt={item.file.name} style={styles.imageThumb} />
                  <div style={styles.imageName}>
                    <b style={styles.imageTitle}>{`New photo ${index + 1}`}</b>
                    <div style={styles.imageFileName}>{item.file.name}</div>
                  </div>
                  <button type="button" style={styles.imageRemove} onClick={() => removeNewManagedImage(index)}>Remove</button>
                </div>
              ))}
            </div>
            {!existingManagedImages.length && !selectedImagePreviews.length && (
              <small style={styles.help}>No review photos selected yet.</small>
            )}
          </Field>
          <Field label="Review Comment" full help="Full review text shown on product detail.">
            <textarea style={styles.textarea} value={form.comment} onChange={(e) => updateForm("comment", e.target.value)} />
          </Field>
        </div>
      );
    }

    if (activeTab === "banner") {
      return (
        <div style={styles.formGrid}>
          <Field label="Title" help="Main banner text, for example 50% OFF.">
            <input style={styles.input} value={form.title} onChange={(e) => updateForm("title", e.target.value)} />
          </Field>
          <Field label="Subtitle" help="Small banner heading, for example DIVINE OFFER. Optional.">
            <input style={styles.input} value={form.subtitle} onChange={(e) => updateForm("subtitle", e.target.value)} />
          </Field>
          <Field label="CTA Text" help="Button text, for example Shop Now. Optional.">
            <input style={styles.input} value={form.ctaText} onChange={(e) => updateForm("ctaText", e.target.value)} />
          </Field>
          <Field label="Product" help="When the banner is tapped, the app can open this selected product detail screen directly.">
            <select style={styles.input} value={form.productId} onChange={(e) => updateForm("productId", e.target.value)}>{renderSelectOptions(productOptions)}</select>
          </Field>
          <Field label="Category" help="If no product is selected, the app can open this category's product list.">
            <select style={styles.input} value={form.categoryId} onChange={(e) => updateForm("categoryId", e.target.value)}>{renderSelectOptions(categoryOptions)}</select>
          </Field>
          <Field label="Starts At" help="When the banner becomes active. Leave blank to make it active immediately.">
            <input type="datetime-local" style={styles.input} value={form.startsAt} onChange={(e) => updateForm("startsAt", e.target.value)} />
          </Field>
          <Field label="Ends At" help="When the banner should stop showing. Useful for limited-time banners.">
            <input type="datetime-local" style={styles.input} value={form.endsAt} onChange={(e) => updateForm("endsAt", e.target.value)} />
          </Field>
          <Field label="Banner Image" help="Banner background/image.">
            <input type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
          </Field>
        </div>
      );
    }

    const selectedDiscountProduct = products.find((item) => item._id === (splitIds(form.productIds)[0] || ""));

    return (
      <div style={styles.formGrid}>
        {activeTab === "coupon" && <Field label="Coupon Code" help="The code users enter in the app, for example DIVINE20."><input style={styles.input} value={form.code} onChange={(e) => updateForm("code", e.target.value.toUpperCase())} /></Field>}
        <Field label="Title" help={activeTab === "coupon" ? "Display name for this coupon in admin or app views." : "Offer name shown as the offer label."}><input style={styles.input} value={form.title} onChange={(e) => updateForm("title", e.target.value)} /></Field>
        <Field label="Discount Type" help="Percentage means %, Flat Amount means fixed rupees off.">
          <select style={styles.input} value={form.discountType} onChange={(e) => updateForm("discountType", e.target.value)}><option value="PERCENTAGE">Percentage</option><option value="FLAT">Flat Amount</option></select>
        </Field>
        <Field label="Discount Value" help="Example: 20 with Percentage means 20% off. 500 with Flat Amount means Rs 500 off.">
          <input type="number" style={styles.input} value={form.discountValue} onChange={(e) => updateForm("discountValue", e.target.value)} />
        </Field>
        {activeTab === "offer" && <Field label="Priority" help="If multiple offers match, the offer with the higher priority is applied first."><input type="number" style={styles.input} value={form.priority} onChange={(e) => updateForm("priority", e.target.value)} /></Field>}
        {activeTab === "coupon" && <Field label="Min Order Amount" help="Minimum cart amount required to apply this coupon. Optional."><input type="number" style={styles.input} value={form.minOrderAmount} onChange={(e) => updateForm("minOrderAmount", e.target.value)} /></Field>}
        {activeTab === "coupon" && <Field label="Usage Limit" help="Total number of times this coupon can be used. Use 0 or blank for unlimited."><input type="number" style={styles.input} value={form.usageLimit} onChange={(e) => updateForm("usageLimit", e.target.value)} /></Field>}
        {activeTab === "coupon" && (
          <Field label="Consultant Scope" help="Choose No Consultant for company coupons. Choose Specific Consultants only when selected consultants will promote/share this coupon. Consultant coupons work only if the user has completed at least 25 minutes of consultation with that consultant.">
            <select style={styles.input} value={form.consultantScope} onChange={(e) => updateConsultantScope(e.target.value)}>
              <option value="NO_CONSULTANT">No Consultant / Company Coupon</option>
              <option value="SPECIFIC_CONSULTANTS">Specific Consultants</option>
            </select>
          </Field>
        )}
        {activeTab === "coupon" && form.consultantScope !== "SPECIFIC_CONSULTANTS" && (
          <div style={{ ...styles.meaningNote, ...styles.full, marginBottom: 0 }}>
            This is a company coupon. No consultant will receive share for this coupon.
          </div>
        )}
        {activeTab === "coupon" && form.consultantScope === "SPECIFIC_CONSULTANTS" && (
          <Field label="Consultants" full help="Select the consultant or group of consultants who will share/promote this coupon code. Search works with name, phone, email, consultant ID, or backend ID.">
            {renderEntitySelector({
              value: form.consultantIds,
              options: consultantOptions,
              onChange: (value) => updateForm("consultantIds", value),
              searchValue: consultantSearch,
              setSearchValue: setConsultantSearch,
              placeholder: "Search consultant by name, phone, email, consultant ID, or backend ID",
              idLabel: "Consultant ID",
              disabled: form.consultantScope !== "SPECIFIC_CONSULTANTS",
            })}
          </Field>
        )}
        {activeTab === "coupon" && form.consultantScope === "SPECIFIC_CONSULTANTS" && (
          <Field label="Consultant Share Type" help="How much the selected consultant should receive when this coupon is used.">
            <select style={styles.input} value={form.consultantShareType} onChange={(e) => updateForm("consultantShareType", e.target.value)}>
              <option value="NONE">No Share</option>
              <option value="PERCENTAGE">Percentage</option>
              <option value="FLAT">Flat Amount</option>
            </select>
          </Field>
        )}
        {activeTab === "coupon" && form.consultantScope === "SPECIFIC_CONSULTANTS" && (
          <Field label="Consultant Share Value" help="Example: 10 with Percentage means consultant gets 10%. 500 with Flat Amount means Rs 500.">
            <input type="number" style={styles.input} value={form.consultantShareValue} onChange={(e) => updateForm("consultantShareValue", e.target.value)} />
          </Field>
        )}
        {activeTab === "coupon" && form.consultantScope === "SPECIFIC_CONSULTANTS" && (
          <Field label="Share Calculated On" help="Total Price means share is calculated on order total. Discount Amount means share is calculated on coupon discount only.">
            <select style={styles.input} value={form.consultantShareOn} onChange={(e) => updateForm("consultantShareOn", e.target.value)}>
              <option value="TOTAL_PRICE">Total Price</option>
              <option value="DISCOUNT_AMOUNT">Discount Amount</option>
            </select>
          </Field>
        )}
        {activeTab === "coupon" && form.consultantScope === "SPECIFIC_CONSULTANTS" && (
          <Field label="Min Consultation Minutes" help="User can apply this coupon only after completing this many total minutes of consultation with the selected consultant. Default is 25 minutes.">
            <input type="number" min="0" style={styles.input} value={form.minConsultationMinutes} onChange={(e) => updateForm("minConsultationMinutes", e.target.value)} />
          </Field>
        )}
        {(activeTab === "offer" || activeTab === "coupon") && (
          <Field
            label="User Scope"
            help={activeTab === "offer"
              ? "Choose whether this product offer is visible to all users or only selected users."
              : "For a public video coupon, keep All Users. Use Specific Users only when the coupon should work for selected users."}
          >
            <select style={styles.input} value={form.userScope} onChange={(e) => updateForm("userScope", e.target.value)}>
              <option value="ALL_USERS">All Users</option>
              <option value="SPECIFIC_USERS">Specific Users</option>
            </select>
          </Field>
        )}
        {(activeTab === "offer" || activeTab === "coupon") && (
          <Field
            label={activeTab === "offer" ? "Users Who Can See This Offer" : "Users Who Can Use"}
            full
            help={activeTab === "offer"
              ? "Select user names only if User Scope is Specific Users. The offer will show only to these selected users."
              : "Select user names only if User Scope is Specific Users. Search works with name, phone, email, user ID, or backend ID."}
          >
            {renderEntitySelector({
              value: form.userIds,
              options: userOptions,
              onChange: (value) => updateForm("userIds", value),
              searchValue: userSearch,
              setSearchValue: setUserSearch,
              placeholder: "Search user by name, phone, email, user ID, or backend ID",
              idLabel: "User ID",
              disabled: form.userScope !== "SPECIFIC_USERS",
            })}
          </Field>
        )}
        <Field label="Starts At" help="When this offer or coupon becomes active."><input type="datetime-local" style={styles.input} value={form.startsAt} onChange={(e) => updateForm("startsAt", e.target.value)} /></Field>
        <Field label="Ends At" help="When this offer or coupon expires."><input type="datetime-local" style={styles.input} value={form.endsAt} onChange={(e) => updateForm("endsAt", e.target.value)} /></Field>
        <Field label="Product" help="Select the product where this offer or coupon will apply.">
          <select style={styles.input} value={splitIds(form.productIds)[0] || ""} onChange={(e) => updateForm("productIds", e.target.value)}>
            {renderSelectOptions(productOptions)}
          </select>
        </Field>
        {renderDiscountPricePreview(selectedDiscountProduct, form)}
        <Field label="Description" full help="Short explanation for this offer or coupon."><textarea style={styles.textarea} value={form.description} onChange={(e) => updateForm("description", e.target.value)} /></Field>
      </div>
    );
  };

  const renderPreview = () => {
    if (activeTab === "product") {
      const addons = (form.addons || []).filter((addon) => addon?.title || addon?.price);
      const productTitle = form.title || "Product Name";
      const productPrice = form.price || "0";
      const displayCategory = categoryTitle || "Category";
      return (
        <div style={styles.previewPanel}>
          <div style={styles.label}>Product Preview</div>
          <div style={styles.productPreviewStack}>
            <div>
              <div style={styles.previewSectionTitle}>All Products Screen</div>
              <div style={styles.phone}>
                <div style={styles.appHeader}>
                  <span>=</span>
                  <span>VEDIC MEET</span>
                  <span>Bag</span>
                </div>
                <div style={styles.phoneBody}>
                  <div style={styles.appBanner}>
                    <div style={{ fontSize: 12, fontWeight: 900 }}>{form.compareAtPrice ? "DIVINE OFFER" : "STORE OFFER"}</div>
                    <div style={{ fontSize: 23, fontWeight: 900 }}>{form.compareAtPrice ? "Limited Deal" : "Featured Product"}</div>
                    <div style={{ fontSize: 12 }}>on {displayCategory}</div>
                    <button type="button" style={{ border: 0, borderRadius: 16, padding: "6px 14px", color: ADMIN_PRIMARY_DARK, marginTop: 9 }}>Shop Now</button>
                  </div>
                  <div style={styles.appCategoryRow}>
                    {["All", "Bracelets", displayCategory, "Gemstone"].map((item, index) => (
                      <div key={`${item}-${index}`} style={{ ...styles.appCategoryChip, ...(index === 0 ? styles.appCategoryChipActive : {}) }}>
                        <div style={{ fontWeight: 900 }}>{index === 0 ? "All" : item.slice(0, 2).toUpperCase()}</div>
                        <div>{item}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ color: "#7a0000", fontSize: 16, marginBottom: 10 }}>Divine Collection</div>
                  <div style={styles.productCardGrid}>
                    <div style={styles.productCard}>
                      {previewImage ? (
                        <img src={previewImage} alt="Product card preview" style={{ ...styles.previewImage, borderRadius: 6 }} />
                      ) : (
                        <div style={{ ...styles.previewImage, borderRadius: 6, display: "grid", placeItems: "center", color: "#9b8d89" }}>Image</div>
                      )}
                      <div style={{ color: "#b28c2d", fontSize: 10, fontWeight: 800, textTransform: "uppercase", marginTop: 6 }}>{displayCategory}</div>
                      <div style={{ color: "#7a0000", fontWeight: 800, fontSize: 13 }}>{productTitle}</div>
                      <div style={{ color: "#222", fontWeight: 900, fontSize: 13 }}>Rs {productPrice}</div>
                      <button type="button" style={{ ...styles.previewButton, background: "#7a0000", padding: "7px 8px", marginTop: 8, fontSize: 11 }}>View Details</button>
                    </div>
                    <div style={styles.productCard}>
                      <div style={{ ...styles.previewImage, borderRadius: 6, display: "grid", placeItems: "center", color: "#9b8d89" }}>Next</div>
                      <div style={{ color: "#b28c2d", fontSize: 10, fontWeight: 800, textTransform: "uppercase", marginTop: 6 }}>Category</div>
                      <div style={{ color: "#7a0000", fontWeight: 800, fontSize: 13 }}>Another Product</div>
                      <div style={{ color: "#222", fontWeight: 900, fontSize: 13 }}>Rs 0</div>
                      <button type="button" style={{ ...styles.previewButton, background: "#7a0000", padding: "7px 8px", marginTop: 8, fontSize: 11 }}>View Details</button>
                    </div>
                  </div>
                </div>
                <div style={styles.appBottomNav}>
                  <span>Store</span>
                  <span>Orders</span>
                  <span>Cart</span>
                  <span>Profile</span>
                </div>
              </div>
            </div>
            <div>
              <div style={styles.previewSectionTitle}>Single Product Detail Screen</div>
              <div style={styles.phone}>
                <div style={styles.phoneBody}>
                  {previewImage ? (
                    <img src={previewImage} alt="Product preview" style={styles.previewImage} />
                  ) : (
                    <div style={{ ...styles.previewImage, display: "grid", placeItems: "center", color: "#9b8d89" }}>Image</div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "start", marginTop: 12 }}>
                    <div>
                      <div style={{ color: "#b28c2d", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>{displayCategory}</div>
                      <div style={{ ...styles.previewTitle, color: "#7a0000", fontSize: 20 }}>{productTitle}</div>
                    </div>
                    <div style={{ ...styles.previewPrice, color: "#7a0000" }}>Rs {productPrice}</div>
                  </div>
                  {form.compareAtPrice && Number(form.compareAtPrice) > Number(form.price || 0) && (
                    <div style={{ color: "#8e8e8e", textDecoration: "line-through", fontSize: 12 }}>MRP Rs {form.compareAtPrice}</div>
                  )}
                  <p style={{ color: "#806a65", fontSize: 13, margin: "10px 0 16px" }}>
                    {form.shortDescription || "Short description will appear here on product listing and detail screen."}
                  </p>
                  {addons.length > 0 && (
                    <div style={{ margin: "12px 0" }}>
                      <div style={{ color: "#7a0000", fontSize: 11, fontWeight: 900, letterSpacing: 1 }}>CUSTOMIZE YOUR EXPERIENCE</div>
                      {addons.map((addon, index) => (
                        <div key={`${addon.title}-${index}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", borderRadius: 8, padding: "9px 10px", marginTop: 7 }}>
                          <span>{addon.title || "Addon option"}</span>
                          <b style={{ color: "#7a0000" }}>+Rs {addon.price || 0}</b>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", borderRadius: 10, padding: "12px", margin: "14px 0" }}>
                    <b style={{ color: "#7a0000" }}>QUANTITY</b>
                    <span style={{ color: "#7a0000", fontWeight: 900 }}>- &nbsp; 1 &nbsp; +</span>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 86px", gap: 8, marginBottom: 12 }}>
                    <input disabled value="Coupon Code (e.g. DIVINE20)" style={{ ...styles.input, background: "#fff" }} />
                    <button type="button" style={{ ...styles.previewButton, background: "#ffd55c", color: "#7a0000" }}>Apply</button>
                  </div>
                  <button type="button" style={{ ...styles.previewButton, background: "#7a0000" }}>Add to Sacred Bag</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "category") {
      return (
        <div style={styles.previewPanel}>
          <div style={styles.label}>App Preview</div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", background: "#fff", borderRadius: 8, padding: 12 }}>
            <div style={{ width: 54, height: 54, borderRadius: 8, background: ADMIN_PRIMARY, color: "#fff", display: "grid", placeItems: "center", fontWeight: 900 }}>
              {form.icon || (form.title || "All").slice(0, 2)}
            </div>
            <div>
              <b style={{ color: ADMIN_PRIMARY_DARK }}>{form.title || "Category Name"}</b>
              <div style={{ color: "#806a65", fontSize: 12 }}>{form.description || "Category description"}</div>
            </div>
          </div>
        </div>
      );
    }

    if (activeTab === "banner") {
      return (
        <div style={styles.previewPanel}>
          <div style={styles.label}>App Preview</div>
          <div style={{ borderRadius: 10, overflow: "hidden", background: ADMIN_PRIMARY, color: "#fff", position: "relative", minHeight: 150 }}>
            {previewImage && <img src={previewImage} alt="Banner preview" style={{ width: "100%", height: 150, objectFit: "cover", opacity: 0.78 }} />}
            <div style={{ position: previewImage ? "absolute" : "static", inset: 0, padding: 16 }}>
              <div style={{ color: "#fff", fontSize: 12, fontWeight: 800 }}>{form.subtitle || "DIVINE OFFER"}</div>
              <div style={{ fontSize: 24, fontWeight: 900 }}>{form.title || "Banner Title"}</div>
              <button type="button" style={{ marginTop: 12, border: 0, borderRadius: 16, padding: "7px 13px", color: ADMIN_PRIMARY_DARK }}>{form.ctaText || "Shop Now"}</button>
            </div>
          </div>
        </div>
      );
    }

    const isCoupon = activeTab === "coupon";
    const scopedProducts = selectedNames(form.productIds, productOptions);
    const scopedCategories = selectedNames(form.categoryIds, categoryOptions);
    const scopedConsultants = selectedNames(form.consultantIds, consultantOptions);
    const scopedUsers = selectedNames(form.userIds, userOptions);
    return (
      <div style={styles.previewPanel}>
        <div style={styles.label}>{isCoupon ? "Coupon Preview" : "Offer Preview"}</div>
        <div style={{ background: "#fff", borderRadius: 8, padding: 14, border: "1px dashed #d7beb8" }}>
          {isCoupon && <div style={{ color: ADMIN_PRIMARY_DARK, fontSize: 22, fontWeight: 900 }}>{form.code || "COUPONCODE"}</div>}
          <div style={styles.previewTitle}>{form.title || (isCoupon ? "Coupon Title" : "Offer Title")}</div>
          <div style={styles.previewPrice}>
            {form.discountValue || 0}{form.discountType === "PERCENTAGE" ? "% OFF" : " Rs OFF"}
          </div>
          <p style={{ color: "#806a65", fontSize: 13 }}>{form.description || "Discount description will show here."}</p>
          <div style={{ ...styles.badge, display: "inline-block" }}>{form.scope?.replace("_", " ")}</div>
          {form.scope === "PRODUCT" && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Products: {scopedProducts.length ? scopedProducts.join(", ") : "No product selected"}
            </div>
          )}
          {form.scope === "CATEGORY" && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              Categories: {scopedCategories.length ? scopedCategories.join(", ") : "No category selected"}
            </div>
          )}
          {isCoupon && form.minOrderAmount && <div style={{ marginTop: 8, fontSize: 12 }}>Minimum order: Rs {form.minOrderAmount}</div>}
          <div style={{ marginTop: 8, fontSize: 12 }}>
            Users: {form.userScope === "SPECIFIC_USERS" ? (scopedUsers.join(", ") || "No user selected") : "All Users"}
          </div>
          {isCoupon && (
            <>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Consultants: {form.consultantScope === "SPECIFIC_CONSULTANTS" ? (scopedConsultants.join(", ") || "No consultant selected") : "No Consultant / Company Coupon"}
              </div>
              <div style={{ marginTop: 8, fontSize: 12 }}>
                Consultant Share: {form.consultantScope !== "SPECIFIC_CONSULTANTS" || form.consultantShareType === "NONE" ? "No Share" : `${form.consultantShareValue || 0}${form.consultantShareType === "PERCENTAGE" ? "%" : " Rs"} on ${form.consultantShareOn === "TOTAL_PRICE" ? "Total Price" : "Discount Amount"}`}
              </div>
              {form.consultantScope === "SPECIFIC_CONSULTANTS" && (
                <div style={{ marginTop: 8, fontSize: 12 }}>
                  Min consultation: {form.minConsultationMinutes || 25} minutes with the selected consultant
                </div>
              )}
            </>
          )}
          <div style={{ marginTop: 8, fontSize: 12 }}>Starts: {formatDateTime(form.startsAt)}</div>
          <div style={{ marginTop: 8, fontSize: 12 }}>Ends: {formatDateTime(form.endsAt)}</div>
          <div style={styles.countdownBadge}>Expiry: {getTimeLeft(form.endsAt, nowTick)}</div>
        </div>
      </div>
    );
  };

  const renderAppPreviewTab = () => {
    const previewProduct = products?.[0] || {};
    const previewCategory = previewProduct?.categoryId?.title || categories?.[0]?.title || "Category";
    const previewProductTitle = previewProduct?.title || "Product Name";
    const previewProductPrice = previewProduct?.price || "0";
    const previewProductImage = previewProduct?.primaryImageUrl || previewProduct?.imageUrls?.[0] || "";
    const previewAddons = previewProduct?.addons?.length
      ? previewProduct.addons
      : [
        { title: "Vedic Energization", price: 500 },
        { title: "Authenticity Certificate", price: 250 },
        { title: "Premium Silk Packaging", price: 150 },
      ];

    return (
      <div style={styles.appPreviewSection}>
        <div style={{ ...styles.label, marginBottom: 12 }}>App Preview</div>
        <div style={styles.appPreviewGrid}>
          <div>
            <div style={styles.previewSectionTitle}>All Products Screen</div>
            <div style={styles.phone}>
              <div style={styles.appHeader}>
                <span>=</span>
                <span>VEDIC MEET</span>
                <span>Bag</span>
              </div>
              <div style={styles.phoneBody}>
                <div style={styles.appBanner}>
                  <div style={{ fontSize: 12, fontWeight: 900 }}>DIVINE OFFER</div>
                  <div style={{ fontSize: 23, fontWeight: 900 }}>50% OFF</div>
                  <div style={{ fontSize: 12 }}>on {previewCategory}</div>
                  <button type="button" style={{ border: 0, borderRadius: 16, padding: "6px 14px", color: ADMIN_PRIMARY_DARK, marginTop: 9 }}>Shop Now</button>
                </div>
                <div style={styles.appCategoryRow}>
                  {["All", "Bracelets", previewCategory, "Gemstone"].map((item, index) => (
                    <div key={`${item}-${index}`} style={{ ...styles.appCategoryChip, ...(index === 0 ? styles.appCategoryChipActive : {}) }}>
                      <div style={{ fontWeight: 900 }}>{index === 0 ? "All" : item.slice(0, 2).toUpperCase()}</div>
                      <div>{item}</div>
                    </div>
                  ))}
                </div>
                <div style={{ color: "#7a0000", fontSize: 16, marginBottom: 10 }}>Divine Collection</div>
                <div style={styles.productCardGrid}>
                  <div style={styles.productCard}>
                    {previewProductImage ? (
                      <img src={previewProductImage} alt="Product card preview" style={{ ...styles.previewImage, borderRadius: 6 }} />
                    ) : (
                      <div style={{ ...styles.previewImage, borderRadius: 6, display: "grid", placeItems: "center", color: "#9b8d89" }}>Image</div>
                    )}
                    <div style={{ color: "#b28c2d", fontSize: 10, fontWeight: 800, textTransform: "uppercase", marginTop: 6 }}>{previewCategory}</div>
                    <div style={{ color: "#7a0000", fontWeight: 800, fontSize: 13 }}>{previewProductTitle}</div>
                    <div style={{ color: "#222", fontWeight: 900, fontSize: 13 }}>Rs {previewProductPrice}</div>
                    <button type="button" style={{ ...styles.previewButton, background: "#7a0000", padding: "7px 8px", marginTop: 8, fontSize: 11 }}>View Details</button>
                  </div>
                  <div style={styles.productCard}>
                    <div style={{ ...styles.previewImage, borderRadius: 6, display: "grid", placeItems: "center", color: "#9b8d89" }}>Next</div>
                    <div style={{ color: "#b28c2d", fontSize: 10, fontWeight: 800, textTransform: "uppercase", marginTop: 6 }}>Category</div>
                    <div style={{ color: "#7a0000", fontWeight: 800, fontSize: 13 }}>Another Product</div>
                    <div style={{ color: "#222", fontWeight: 900, fontSize: 13 }}>Rs 0</div>
                    <button type="button" style={{ ...styles.previewButton, background: "#7a0000", padding: "7px 8px", marginTop: 8, fontSize: 11 }}>View Details</button>
                  </div>
                </div>
              </div>
              <div style={styles.appBottomNav}>
                <span>Store</span>
                <span>Orders</span>
                <span>Cart</span>
                <span>Profile</span>
              </div>
            </div>
          </div>
          <div>
            <div style={styles.previewSectionTitle}>Single Product Detail Screen</div>
            <div style={styles.phone}>
              <div style={styles.phoneBody}>
                {previewProductImage ? (
                  <img src={previewProductImage} alt="Product preview" style={styles.previewImage} />
                ) : (
                  <div style={{ ...styles.previewImage, display: "grid", placeItems: "center", color: "#9b8d89" }}>Image</div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "start", marginTop: 12 }}>
                  <div>
                    <div style={{ color: "#b28c2d", fontSize: 11, fontWeight: 800, textTransform: "uppercase" }}>{previewCategory}</div>
                    <div style={{ ...styles.previewTitle, color: "#7a0000", fontSize: 20 }}>{previewProductTitle}</div>
                  </div>
                  <div style={{ ...styles.previewPrice, color: "#7a0000" }}>Rs {previewProductPrice}</div>
                </div>
                {previewProduct?.compareAtPrice && Number(previewProduct.compareAtPrice) > Number(previewProductPrice || 0) && (
                  <div style={{ color: "#8e8e8e", textDecoration: "line-through", fontSize: 12 }}>MRP Rs {previewProduct.compareAtPrice}</div>
                )}
                <p style={{ color: "#806a65", fontSize: 13, margin: "10px 0 16px" }}>
                  {previewProduct?.shortDescription || "Short description will appear here on product listing and detail screen."}
                </p>
                <div style={{ margin: "12px 0" }}>
                  <div style={{ color: "#7a0000", fontSize: 11, fontWeight: 900, letterSpacing: 1 }}>CUSTOMIZE YOUR EXPERIENCE</div>
                  {previewAddons.map((addon, index) => (
                    <div key={`${addon.title}-${index}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", borderRadius: 8, padding: "9px 10px", marginTop: 7 }}>
                      <span>{addon.title || "Addon option"}</span>
                      <b style={{ color: "#7a0000" }}>+Rs {addon.price || 0}</b>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", borderRadius: 10, padding: "12px", margin: "14px 0" }}>
                  <b style={{ color: "#7a0000" }}>QUANTITY</b>
                  <span style={{ color: "#7a0000", fontWeight: 900 }}>- &nbsp; 1 &nbsp; +</span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 86px", gap: 8, marginBottom: 12 }}>
                  <input disabled value="Coupon Code (e.g. DIVINE20)" style={{ ...styles.input, background: "#fff" }} />
                  <button type="button" style={{ ...styles.previewButton, background: "#ffd55c", color: "#7a0000" }}>Apply</button>
                </div>
                <button type="button" style={{ ...styles.previewButton, background: "#7a0000" }}>Add to Sacred Bag</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderRows = () => {
    if (activeTab === "order") return renderOrderRows();
    if (activeTab === "tracking") return renderTrackingRows();

    const isDiscountTab = activeTab === "offer" || activeTab === "coupon";
    const isReadOnlyTab = activeTab === "order" || activeTab === "tracking";
    const showsValidity = isDiscountTab || activeTab === "banner";
    const isCategoryTab = activeTab === "category";
    const showsImage = !isDiscountTab && !isReadOnlyTab;
    const productCellStyle = (style) => (activeTab === "product" ? style : undefined);
    if (!list.length) {
      return <NotFound cols={isCategoryTab ? 5 : showsValidity && !isDiscountTab ? 9 : 8} msg="No ecommerce records found!" icon={NOT_FOUND} loader={loader} />;
    }
    return list.map((item, index) => (
      <tr key={item._id}>
        <td style={productCellStyle({ width: 52, verticalAlign: "middle" })}>{findSerialNumber(index, activePage, limit)}</td>
        {showsImage && <td style={productCellStyle({ width: 78, verticalAlign: "middle" })}>{item.imageUrl || item.primaryImageUrl || item.imageUrls?.[0] ? <Avatar variant="square" src={item.imageUrl || item.primaryImageUrl || item.imageUrls?.[0]} sx={{ width: 48, height: 48, borderRadius: 1 }} /> : "---"}</td>}
        <td style={productCellStyle({ width: 460, minWidth: 460, verticalAlign: "middle" })}>
          {activeTab === "product" ? (
            <div style={styles.productNameCell}>
              <b style={styles.productTitle}>{item.title || "---"}</b>
              {item.description && <div style={styles.productDescriptionClamp}>{item.description}</div>}
            </div>
          ) : (
            <>
              <b>{activeTab === "review" ? (item.reviewerName || "---") : item.title || item.code || item.orderNo || item.eventType || "---"}</b>
              {item.description && <div style={styles.detailText}>{item.description}</div>}
            </>
          )}
          {activeTab === "order" && renderDetailLine("User", personLabel(item.userId))}
          {activeTab === "tracking" && renderDetailLine("Event ID", item._id)}
          {activeTab === "tracking" && renderDetailLine("User", personLabel(item.userId))}
          {activeTab === "tracking" && renderDetailLine("User ID", recordId(item.userId))}
          {activeTab === "tracking" && renderDetailLine("Screen", item.screen)}
          {activeTab === "coupon" && item.code && <div style={styles.detailText}>Code: {item.code}</div>}
          {activeTab === "review" && renderDetailLine("Location", item.reviewerLocation)}
          {activeTab === "review" && renderDetailLine("Title", item.title)}
          {activeTab === "review" && item.comment && <div style={styles.detailText}>{item.comment}</div>}
        </td>
        {!isCategoryTab && (
          <td style={productCellStyle({ width: 150, verticalAlign: "middle" })}>
            {activeTab === "product" ? (
              <div style={styles.productCategoryCell}>
                <div>{item.categoryId?.title || "---"}</div>
                {renderDetailLine("Users", item.userScope === "SPECIFIC_USERS" ? namesFromRecords(item.userIds) : "All Users")}
              </div>
            ) : (
              <>
            {activeTab === "order" ? `${item.items?.length || 0} item(s)` : activeTab === "tracking" ? (item.productId?.title || item.orderId?.orderNo || "---") : activeTab === "review" ? item.productId?.title || "---" : item.categoryId?.title || item.scope || "---"}
            {activeTab === "tracking" && renderDetailLine("Product ID", recordId(item.productId))}
            {activeTab === "tracking" && item.productId?.price !== undefined && renderDetailLine("Product Price", `Rs ${item.productId.price}`)}
            {activeTab === "tracking" && renderDetailLine("Cart Item ID", item.cartItemId)}
            {activeTab === "tracking" && renderDetailLine("Order", item.orderId?.orderNo || recordId(item.orderId))}
            {activeTab === "tracking" && item.orderId?.total !== undefined && renderDetailLine("Order Total", `Rs ${item.orderId.total}`)}
            {activeTab === "tracking" && item.orderId?.orderStatus && renderDetailLine("Order Status", item.orderId.orderStatus)}
            {activeTab === "order" && renderDetailLine("Products", (item.items || []).map((orderItem) => orderItem.title).join(", "))}
            {activeTab === "banner" && renderDetailLine("Product", item.productId?.title || "No product selected")}
            {activeTab === "banner" && renderDetailLine("Category", item.categoryId?.title || "No category selected")}
            {isDiscountTab && item.scope === "PRODUCT" && renderDetailLine("Products", namesFromRecords(item.productIds))}
            {isDiscountTab && item.scope === "CATEGORY" && renderDetailLine("Categories", namesFromRecords(item.categoryIds))}
            {activeTab === "coupon" && renderDetailLine("Consultants", item.consultantScope === "SPECIFIC_CONSULTANTS" ? namesFromRecords(item.consultantIds) : "No Consultant / Company Coupon")}
            {activeTab === "coupon" && item.consultantScope === "SPECIFIC_CONSULTANTS" && renderDetailLine("Min Consultation", `${item.minConsultationMinutes || 25} minutes`)}
            {isDiscountTab && renderDetailLine("Users", item.userScope === "SPECIFIC_USERS" ? namesFromRecords(item.userIds) : "All Users")}
              </>
            )}
          </td>
        )}
        {!isCategoryTab && (
          <td style={productCellStyle({ width: 700, verticalAlign: "middle" })}>
            {activeTab === "product" ? renderProductDiscounts(item) : activeTab === "order" ? `Rs ${item.total || 0}` : activeTab === "tracking" ? <pre style={styles.jsonBlock}>{fullJson(item.metadata)}</pre> : activeTab === "review" ? `${item.rating || 0} ★` : item.price ? `Rs ${item.price}` : item.discountValue ? `${item.discountValue}${item.discountType === "PERCENTAGE" ? "%" : " Rs"}` : "---"}
            {activeTab === "order" && renderDetailLine("Subtotal", `Rs ${item.subtotal || 0}`)}
            {activeTab === "order" && renderDetailLine("GST", `Rs ${item.gstTotal || 0}`)}
            {activeTab === "order" && renderDetailLine("Discount", `Rs ${item.discountAmount || 0}`)}
            {activeTab === "order" && item.couponCode && renderDetailLine("Coupon", item.couponCode)}
            {isDiscountTab && renderDetailLine("Type", item.discountType === "PERCENTAGE" ? "Percentage" : "Flat Amount")}
            {activeTab === "coupon" && renderDetailLine("Min Order", item.minOrderAmount ? `Rs ${item.minOrderAmount}` : "No minimum")}
            {activeTab === "coupon" && renderDetailLine(
              "Consultant Share",
              item.consultantScope !== "SPECIFIC_CONSULTANTS" || item.consultantShareType === "NONE"
                ? "No Share"
                : `${item.consultantShareValue || 0}${item.consultantShareType === "PERCENTAGE" ? "%" : " Rs"} on ${item.consultantShareOn === "TOTAL_PRICE" ? "Total Price" : "Discount Amount"}`
            )}
          </td>
        )}
        {!isCategoryTab && (
          <td style={productCellStyle({ width: 90, verticalAlign: "middle" })}>
            {activeTab === "product" ? <div style={styles.productStockCell}>{item.stock ?? "---"}</div> : (
              <>
            {activeTab === "order" ? item.paymentMethod : activeTab === "tracking" ? item.platform || "---" : activeTab === "review" ? (item.isVerifiedBuyer ? "Verified Buyer" : "Manual Review") : activeTab === "coupon" ? `${getCouponUsageInfo(item).usedCount}/${getCouponUsageInfo(item).usageLimit || "Unlimited"}` : item.stock ?? item.usageLimit ?? item.priority ?? "---"}
            {activeTab === "tracking" && renderDetailLine("Device ID", item.deviceId)}
            {activeTab === "tracking" && renderDetailLine("Created", formatDateTime(item.createdAt))}
            {activeTab === "tracking" && renderDetailLine("Updated", formatDateTime(item.updatedAt))}
            {activeTab === "order" && renderDetailLine("Payment", item.paymentStatus)}
            {activeTab === "offer" && renderDetailLine("Priority", item.priority ?? 0)}
            {activeTab === "coupon" && renderDetailLine("Used", getCouponUsageInfo(item).usedCount)}
            {activeTab === "coupon" && getCouponUsageInfo(item).reservedCount > 0 && renderDetailLine("Reserved", getCouponUsageInfo(item).reservedCount)}
            {activeTab === "coupon" && renderDetailLine("Usage Limit", getCouponUsageInfo(item).usageLimit || "Unlimited")}
            {activeTab === "coupon" && getCouponUsageInfo(item).remaining !== null && renderDetailLine("Remaining", getCouponUsageInfo(item).remaining)}
            {activeTab === "banner" && renderDetailLine("Sort Order", item.sortOrder ?? 0)}
            {activeTab === "review" && renderDetailLine("Review Date", formatDateTime(item.reviewDate))}
            {activeTab === "review" && renderDetailLine("Sort Order", item.sortOrder ?? 0)}
              </>
            )}
          </td>
        )}
        {showsValidity && (
          <td>
            {renderDetailLine("Starts", formatDateTime(item.startsAt))}
            {renderDetailLine("Ends", formatDateTime(item.endsAt))}
            <div style={styles.countdownBadge}>{getTimeLeft(item.endsAt, nowTick)}</div>
          </td>
        )}
        <td style={productCellStyle({ width: 86, verticalAlign: "middle" })}><span style={styles.badge}>{activeTab === "order" ? item.orderStatus : activeTab === "tracking" ? "Saved" : item.status ? "Active" : "Inactive"}</span></td>
        <td style={productCellStyle({ width: 118, verticalAlign: "middle" })}>
          {isReadOnlyTab ? "---" : (
            <div className="action_button">
              <IconButton onClick={() => openEdit(item)}><EditIcon className="edit_icon" /></IconButton>
              <IconButton onClick={() => setDeleteTarget(item)}><DeleteIcon color="error" /></IconButton>
              <Switch checked={item.status} onChange={() => statusHandler(item)} />
            </div>
          )}
        </td>
      </tr>
    ));
  };

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Ecommerce Management</h4>
            {!["appPreview", "order", "tracking"].includes(activeTab) && (
              <div className="block_user_button">
                <button onClick={openAdd}><AddIcon />Add {tabs.find((tab) => tab.key === activeTab)?.label}</button>
              </div>
            )}
          </div>

          <div style={styles.tabs}>
            {tabs.map((tab) => (
              <button
                key={tab.key}
                style={{ ...styles.tab, ...(activeTab === tab.key ? styles.activeTab : {}) }}
                onClick={() => {
                  setActiveTab(tab.key);
                  setActivePage(1);
                  setSearch("");
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === "appPreview" ? (
            renderAppPreviewTab()
          ) : (
            <>
              <div className="FilterBox FilterArea">
                <div className="form-group search_lg_size userSearch">
                  <input
                    type="search"
                    className="form-control"
                    placeholder={`Search ${activeTab}`}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setActivePage(1);
                    }}
                  />
                  <span className="user_search_icon">{common.SEARCH_ICON}</span>
                </div>
              </div>

              <div className="content-table">
                <table style={activeTab === "product" ? { minWidth: 1730, tableLayout: "fixed" } : undefined}>
                  <thead>
                    <tr>
                      <th style={activeTab === "product" ? { width: 52 } : undefined}>S. No.</th>
                      {activeTab === "order" ? (
                        <>
                          <th>Order / User</th>
                          <th>Items / Addons</th>
                          <th>Amount / Coupon</th>
                          <th>Shipping</th>
                          <th>Payment / Time</th>
                          <th>Status</th>
                          <th>Action</th>
                        </>
                      ) : (
                        <>
                          {!["offer", "coupon", "tracking"].includes(activeTab) && <th style={activeTab === "product" ? { width: 78 } : undefined}>Image</th>}
                          <th style={activeTab === "product" ? { width: 460 } : undefined}>{activeTab === "tracking" ? "Event / User" : "Name / Code"}</th>
                          {activeTab !== "category" && <th style={activeTab === "product" ? { width: 150 } : undefined}>{activeTab === "tracking" ? "Product / Order" : "Category / Scope"}</th>}
                          {activeTab !== "category" && <th style={activeTab === "product" ? { width: 700 } : undefined}>{activeTab === "tracking" ? "Metadata" : "Price / Discount"}</th>}
                          {activeTab !== "category" && <th style={activeTab === "product" ? { width: 90, textAlign: "center" } : undefined}>{activeTab === "tracking" ? "Device / Time" : "Stock / Limit"}</th>}
                          {(activeTab === "offer" || activeTab === "coupon" || activeTab === "banner") && <th>Validity / Expiry</th>}
                          <th style={activeTab === "product" ? { width: 86 } : undefined}>Status</th>
                          <th style={activeTab === "product" ? { width: 118 } : undefined}>Action</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>{renderRows()}</tbody>
                </table>
              </div>

              {total > limit && (
                <div className="pagination-wrapper">
                  <div className="Pagination">
                    <Pagination activePage={activePage} totalItemsCount={total} itemsCountPerPage={limit} onChange={setActivePage} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ModalBox
        status={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${selected ? "Edit" : "Add"} ${tabs.find((tab) => tab.key === activeTab)?.label}`}
        action={submitHandler}
        closeIcon
        size="xl"
      >
        {/* <div style={styles.meaningNote}>
          App navigation uses the selected Product or Category. For banners, selecting a Product or Category is enough.
        </div> */}
        <button type="button" style={styles.testDataButton} onClick={fillTestData}>
          Fill Test Data
        </button>
        <button type="button" style={styles.previewToggle} onClick={() => setShowModalPreview((prev) => !prev)}>
          <span>App Preview</span>
          <span>{showModalPreview ? "Hide" : "Show"}</span>
        </button>
        <div style={showModalPreview ? styles.modalLayoutWithPreview : styles.modalLayout}>
          {renderForm()}
          {showModalPreview && renderPreview()}
        </div>
      </ModalBox>

      <ModalBox
        status={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete Record"
        description={`Are you sure you want to delete ${deleteTarget?.title || deleteTarget?.code || "this record"}?`}
        action={deleteHandler}
      />

      <ModalBox
        status={Boolean(cancelTarget)}
        onClose={closeCancelOrder}
        title="Cancel Order"
        description={`Cancel order ${cancelTarget?.orderNo || ""}? This cannot be undone from admin.`}
        action={cancelOrderHandler}
        actionLoading={cancelLoading}
      >
        {cancelTarget?.paymentMethod === "UPI_NETBANKING" && cancelTarget?.paymentStatus === "PAID" && (
          <div style={styles.meaningNote}>
            This is a prepaid paid order. Admin cancel will still be sent to the server. Gateway refund must be handled on the server.
          </div>
        )}
        {!!cancelTarget?.shiprocketShipmentId && (
          <div style={styles.meaningNote}>
            Shiprocket shipment exists. Server must cancel it on Shiprocket before marking this order cancelled.
          </div>
        )}
        <Field label="Cancel reason" help="Optional, saved on the order for audit.">
          <textarea
            style={styles.textarea}
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="Why is this order being cancelled?"
            disabled={cancelLoading}
          />
        </Field>
      </ModalBox>
    </>
  );
};

export default EcommerceManagement;
